import React, { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  handleTextInputChange,
  handleSearchInputChange,
  getCharacterCount,
  validateRequiredFields,
  FIELD_LIMITS,
  sanitizeDescriptionInput
} from '../../../utils/inputValidation';
import {
  User,
  Phone,
  Mail,
  MapPin,
  Building,
  Tag,
  Star,
  AlertTriangle,
  CheckCircle,
  Clock,
  FileText,
  MessageSquare,
  ShoppingCart,
  Calendar,
  Edit,
  Plus,
  Filter,
  MoreVertical,
  ExternalLink,
  Eye,
  Send,
  PhoneCall,
  MessageCircle,
  Mail as MailIcon,
  FileImage,
  Award,
  TrendingUp,
  Package,
  Truck,
  Shield,
  History as HistoryIcon,
  Settings,
  X,
  ArrowLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  IndianRupee,
  Database,
  HardDrive,
  Lock as LockIcon,
  Unlock as UnlockIcon,
  Download,
  Hash,
  Paperclip,
  Trash2,
  ChevronDown,
} from "lucide-react";
import { post, del } from '../../../api/apiClient';
import { API_ENDPOINTS } from '../../../api/config';
import Button from '../../../components/common/Button/Button';
import customerService from '../../../api/crm/customers/customerService';
import PermissionGate from '../../../components/common/PermissionGate';
import quotationService from '../../../api/crm/quotations/quotationService';
import { formatDate } from '../../../utils/stringUtils';
import { getQuotationStatusClasses, formatQuotationStatus } from '../../../utils/quotationStatusUtils';
import { formatIndianAmount, formatIndianNumber } from '../../../utils/amountFormatter';
import SendEmailModal from '../../../components/crm/SendEmailModal';
import WhatsAppModal from '../quotation-proposal/components/WhatsAppModal';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import { Search } from 'lucide-react';
import AddPaymentModal from '../../../components/crm/AddPaymentModal';
import usePermissions from '../../../hooks/usePermissions';
import {
  PAYMENT_METHOD_FILTER_OPTIONS as PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_FILTER_OPTIONS as PAYMENT_STATUS_OPTIONS,
  formatPaymentMethodLabel
} from '../../../constants/paymentMethods';

const getPaymentStatusClasses = (status) => {
  switch (String(status || '').toUpperCase()) {
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PENDING':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// Helper to format address object into readable string
const formatAddress = (addressObj) => {
  if (!addressObj) return '';

  // If it's already a string, check if it's a stringified object
  if (typeof addressObj === 'string') {
    const trimmed = addressObj.trim();
    // If it looks like a stringified object/array, try to parse it
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(trimmed);
        // Recursively call formatAddress with parsed object
        return formatAddress(parsed);
      } catch (e) {
        // If JSON parsing fails, try to extract values using regex
        // Handle format like: "{ street: '', city: '', state: '', pincode: '', country: 'India' }"
        const streetMatch = trimmed.match(/street:\s*['"]([^'"]*)['"]/);
        const cityMatch = trimmed.match(/city:\s*['"]([^'"]*)['"]/);
        const stateMatch = trimmed.match(/state:\s*['"]([^'"]*)['"]/);
        const pincodeMatch = trimmed.match(/pincode:\s*['"]([^'"]*)['"]/);
        const countryMatch = trimmed.match(/country:\s*['"]([^'"]*)['"]/);

        const parts = [];
        if (streetMatch && streetMatch[1] && streetMatch[1].trim()) parts.push(streetMatch[1].trim());
        if (cityMatch && cityMatch[1] && cityMatch[1].trim()) parts.push(cityMatch[1].trim());
        if (stateMatch && stateMatch[1] && stateMatch[1].trim()) parts.push(stateMatch[1].trim());
        if (pincodeMatch && pincodeMatch[1] && pincodeMatch[1].trim()) parts.push(pincodeMatch[1].trim());
        if (countryMatch && countryMatch[1] && countryMatch[1].trim() && countryMatch[1].trim() !== 'India') {
          parts.push(countryMatch[1].trim());
        }

        return parts.length > 0 ? parts.join(', ') : '';
      }
    }
    // If it's a normal string, return it
    return trimmed || '';
  }

  // If it's an object, build formatted string
  if (typeof addressObj === 'object' && addressObj !== null) {
    // Check if it's an array (shouldn't be, but handle it)
    if (Array.isArray(addressObj)) return '';

    const parts = [];
    // Only add non-empty string values, and check for stringified objects
    if (addressObj.street && typeof addressObj.street === 'string') {
      const street = addressObj.street.trim();
      if (street.startsWith('{') || street.startsWith('[')) {
        const parsedStreet = formatAddress(street);
        if (parsedStreet) parts.push(parsedStreet);
      } else if (street) {
        parts.push(street);
      }
    }
    if (addressObj.street2 && typeof addressObj.street2 === 'string' && addressObj.street2.trim()) {
      parts.push(addressObj.street2.trim());
    }
    if (addressObj.street3 && typeof addressObj.street3 === 'string' && addressObj.street3.trim()) {
      parts.push(addressObj.street3.trim());
    }
    if (addressObj.city && typeof addressObj.city === 'string' && addressObj.city.trim() && !addressObj.city.trim().startsWith('{')) {
      parts.push(addressObj.city.trim());
    }
    if (addressObj.state && typeof addressObj.state === 'string' && addressObj.state.trim() && !addressObj.state.trim().startsWith('{')) {
      parts.push(addressObj.state.trim());
    }
    if (addressObj.pincode && typeof addressObj.pincode === 'string' && addressObj.pincode.trim() && !addressObj.pincode.trim().startsWith('{')) {
      parts.push(addressObj.pincode.trim());
    }
    // Only add country if it's not India (to avoid redundancy)
    if (addressObj.country && typeof addressObj.country === 'string' && addressObj.country.trim() && addressObj.country.trim() !== 'India' && !addressObj.country.trim().startsWith('{')) {
      parts.push(addressObj.country.trim());
    }

    // Return formatted string if we have any parts, otherwise empty
    return parts.length > 0 ? parts.join(', ') : '';
  }

  return '';
};

// Helper function to render text with clickable links
const renderReviewText = (text) => {
  if (!text) return null;
  if (typeof text !== 'string') return text;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = text.split(urlRegex);

  return parts.map((part, index) => {
    if (urlRegex.test(part)) {
      const href = part.match(/^www\./i) ? `https://${part}` : part;
      return (
        <a
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-agrosaf-green hover:underline break-all font-medium"
        >
          {part}
        </a>
      );
    }
    return part;
  });
};


// Helper function to transform customer 360 data
// Only transforms customer profile - orders, tickets, metrics, and interactions are fetched via separate APIs
const transformCustomer360Data = (result, id) => {
  if (!result.success || !result.data) {
    return {
      customer: null,
      error: result.error || 'Failed to load customer 360 data'
    };
  }

  const apiCustomer = result.data.customer || null;

  // Map backend customer shape into UI-friendly customer object
  const mappedCustomer = apiCustomer
    ? {
      id: apiCustomer._id || apiCustomer.customerId || id,
      name:
        apiCustomer.contactPerson ||
        apiCustomer.companyName ||
        apiCustomer.name ||
        "N/A",
      businessName: apiCustomer.companyName || "",
      email: apiCustomer.email || "",
      phone: Array.isArray(apiCustomer.phone) ? apiCustomer.phone : (apiCustomer.phone ? [apiCustomer.phone] : []),
      address: (() => {
        try {
          const billing = formatAddress(apiCustomer.billingAddress);
          const shipping = formatAddress(apiCustomer.shippingAddress);
          const direct = formatAddress(apiCustomer.address);
          const finalAddress = billing || shipping || direct || '';

          // Ensure it's always a string, never an object
          if (typeof finalAddress === 'string') {
            return finalAddress.trim() || '';
          }
          // If it's an object, try to format it one more time
          if (typeof finalAddress === 'object' && finalAddress !== null) {
            const reformatted = formatAddress(finalAddress);
            return typeof reformatted === 'string' ? reformatted.trim() : '';
          }
          return '';
        } catch (err) {
          return '';
        }
      })(),
      profileImage: apiCustomer.profileImage || null,
      // Metrics are now fetched via separate KPIs API
      totalOrders: 0,
      totalValue: 0,
      totalTickets: 0,
      openTicketsCount: 0,
      resolvedTicketsCount: 0,
      isBlocked: apiCustomer.isBlocked || false,
      blockReason: apiCustomer.blockReason || "",
      statusHistory: apiCustomer.statusHistory || [],
      createdAt: apiCustomer.createdAt || null,
      createdFromLead: apiCustomer.createdFromLead || null,
      documents: (apiCustomer.customerDocuments || apiCustomer.documents || []).map(doc => ({
        id: doc._id || doc.id,
        type: doc.documentTypeName ||
          (doc.documentTypeId && typeof doc.documentTypeId === 'object' ? doc.documentTypeId.name : (typeof doc.documentTypeId === 'string' ? doc.documentTypeId : 'Document')),
        number: doc.certificateNumber || doc.number || 'N/A',
        expiry: doc.expiryDate || null,
        url: doc.documentLink || (doc.fileInfo?.url || ''),
        method: doc.documentMethod || (doc.fileInfo?.url ? 'upload' : (doc.documentLink ? 'link' : 'upload'))
      }))
    }
    : null;

  return {
    customer: mappedCustomer,
    rawCustomer: apiCustomer, // Include raw customer data for detailed view
    error: null
  };
};

const CUSTOMER_REVIEW_MAX_LENGTH = 2000;

// Lazy-loaded: analytics bundle only downloads when the Analytics tab is opened.
const CustomerAnalytics = lazy(() => import('./CustomerAnalytics'));

const Customer360 = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // Get customer ID from URL params
  const queryClient = useQueryClient();
  const { canCreate } = usePermissions();
  const canCreatePayment = canCreate('customer_payment');
  const [activeTab, setActiveTab] = useState("overview");
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState(null);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsLimit, setPaymentsLimit] = useState(10);
  const [paymentSearch, setPaymentSearch] = useState('');
  const [debouncedPaymentSearch, setDebouncedPaymentSearch] = useState('');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
  const [paymentDateRange, setPaymentDateRange] = useState({ startDate: '', endDate: '' });
  const [errors, setErrors] = useState({});
  const [reviewText, setReviewText] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsLimit, setReviewsLimit] = useState(10);
  const [reviewFile, setReviewFile] = useState(null);
  const [isUploadingReviewFile, setIsUploadingReviewFile] = useState(false);

  // Interactions tab state (similar to Lead 360)
  const [interactionsPage, setInteractionsPage] = useState(1);
  const [allInteractions, setAllInteractions] = useState([]);

  // Quotations tab pagination state
  const [quotationsPage, setQuotationsPage] = useState(1);
  const [quotationsLimit, setQuotationsLimit] = useState(10);

  // Invoices tab pagination state
  const [invoicesPage, setInvoicesPage] = useState(1);
  const [invoicesLimit, setInvoicesLimit] = useState(10);
  const [invoicesSortBy, setInvoicesSortBy] = useState('invoiceDate');
  const [invoicesSortOrder, setInvoicesSortOrder] = useState('desc');
  const [invoiceSearchTerm, setInvoiceSearchTerm] = useState('');
  const [debouncedInvoiceSearchTerm, setDebouncedInvoiceSearchTerm] = useState('');
  const [invoiceDateRange, setInvoiceDateRange] = useState({ startDate: '', endDate: '' });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  // Debounce invoice search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInvoiceSearchTerm(invoiceSearchTerm);
      setInvoicesPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [invoiceSearchTerm]);

  // Selected interaction for detail modal
  const [selectedInteraction, setSelectedInteraction] = useState(null);
  const [showInteractionModal, setShowInteractionModal] = useState(false);

  // Message & Call modal states (reuse Lead 360 style)
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [isSavingActivity, setIsSavingActivity] = useState(false);
  const [messageType, setMessageType] = useState("whatsapp");
  const [messageContent, setMessageContent] = useState("");
  const [messageSubject, setMessageSubject] = useState("");
  const [messageRecipient, setMessageRecipient] = useState("");
  const [callDirection, setCallDirection] = useState("outbound");
  const [callDuration, setCallDuration] = useState("");
  const [callNotes, setCallNotes] = useState("");
  const [callOutcome, setCallOutcome] = useState("");
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDate, setFollowUpDate] = useState("");
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [isSendingWhatsApp, setIsSendingWhatsApp] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const refreshCustomerActivities = async () => {
    setInteractionsPage(1);
    setAllInteractions([]);
    await queryClient.invalidateQueries({ queryKey: ['customerInteractions', id] });
  };

  const createSentCustomerActivity = async ({ type, title, description, notes }) => {
    const result = await customerService.createCustomerActivity(id, {
      type,
      title,
      description,
      outcome: "sent",
      priority: "medium",
      notes,
      performedAt: new Date().toISOString()
    });

    if (!result.success) {
      throw new Error(result.error || "Failed to save customer activity");
    }

    await refreshCustomerActivities();
    return result.activity;
  };

  const handleEmailSentActivity = async (emailData) => {
    try {
      const recipientNotes = [
        emailData.recipient ? `Recipient: ${emailData.recipient}` : "",
        emailData.cc ? `CC: ${emailData.cc}` : "",
        emailData.attachments?.length ? `Attachments: ${emailData.attachments.map(att => att.name).join(", ")}` : ""
      ].filter(Boolean).join("\n");

      await createSentCustomerActivity({
        type: "email",
        title: emailData.subject || "Email sent",
        description: `Subject: ${emailData.subject || ""}\n${emailData.message || "Email sent to customer"}`,
        notes: recipientNotes || undefined
      });
    } catch (error) {
      toast.error(error.message || "Email sent, but activity was not saved");
    }
  };

  // WhatsApp send handler
  const handleWhatsAppSend = async (data) => {
    try {
      setIsSendingWhatsApp(true);

      // Get phone number from customer data
      // mappedCustomer.phone is an array
      const phoneNumber = customer360Data?.customer?.phone[0] || "";

      if (!phoneNumber) {
        toast.error("Customer phone number not found");
        return;
      }

      const payload = {
        customerId: id,
        message: data.message,
        phone: phoneNumber,
      };

      const result = await customerService.sendWhatsApp(payload);
      if (result.success) {
        try {
          await createSentCustomerActivity({
            type: "whatsapp",
            title: "WhatsApp message sent",
            description: data.message || "WhatsApp message sent to customer",
            notes: `Recipient: ${phoneNumber}`
          });
        } catch (activityError) {
          toast.error(activityError.message || "WhatsApp sent, but activity was not saved");
        }
        toast.success(result.message || "WhatsApp message sent successfully");
        setShowWhatsAppModal(false);
      } else {
        toast.error(result.error || "Failed to send WhatsApp message");
      }
    } catch (error) {
      toast.error("An error occurred while sending WhatsApp message");
    } finally {
      setIsSendingWhatsApp(false);
    }
  };

  // Export the currently opened customer's detail as a backend-generated Excel file.
  const handleExportCustomer = async () => {
    if (isExporting) return;
    setIsExporting(true);
    const toastId = toast.loading('Preparing Excel export...');
    try {
      const exportName =
        rawCustomer?.companyName ||
        rawCustomer?.contactPerson ||
        rawCustomer?.customerId ||
        'Customer';
      const result = await customerService.exportCustomer360Detail(id, exportName);
      if (result.success) {
        toast.success('Customer details exported successfully', { id: toastId });
      } else {
        toast.error(result.error || 'Failed to export customer details', { id: toastId });
      }
    } catch (error) {
      toast.error(error.message || 'Failed to export customer details', { id: toastId });
    } finally {
      setIsExporting(false);
    }
  };

  // Redirect if no id
  useEffect(() => {
    if (!id) {
      navigate('/customer_360');
    }
  }, [id, navigate]);

  // Fetch customer 360 data with React Query
  const {
    data: customer360Data,
    isLoading,
    isError,
    error: customer360Error
  } = useQuery({
    queryKey: ['customer360', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomer360(id);
      return transformCustomer360Data(result, id);
    },
    enabled: !!id
  });

  // Fetch customer KPIs separately
  const {
    data: kpisData,
    isLoading: kpisLoading,
    isError: kpisError
  } = useQuery({
    queryKey: ['customerKpis', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerKpis(id);
      if (result.success) {
        return result.kpis;
      }
      throw new Error(result.error || 'Failed to fetch KPIs');
    },
    enabled: !!id
  });

  // Customer financial summary (received + outstanding)
  const {
    data: financialsData,
    isLoading: financialsLoading
  } = useQuery({
    queryKey: ['customerFinancials', id],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerFinancials(id);
      if (result.success) {
        return result.financials;
      }
      throw new Error(result.error || 'Failed to fetch financial summary');
    },
    enabled: !!id
  });

  const financials = financialsData || {
    totalReceivedAmount: 0,
    outstandingAmount: 0,
    totalInvoiceAmount: 0,
    totalPaidInvoices: 0,
    totalPendingInvoices: 0,
    lastPaymentDate: null
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPaymentSearch(paymentSearch.trim());
      setPaymentsPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [paymentSearch]);

  const {
    data: paymentsData,
    isLoading: paymentsLoading,
    isError: paymentsError,
    error: paymentsQueryError
  } = useQuery({
    queryKey: [
      'customerPayments',
      id,
      paymentsPage,
      paymentsLimit,
      debouncedPaymentSearch,
      paymentMethodFilter,
      paymentStatusFilter,
      paymentDateRange
    ],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerPayments(id, {
        page: paymentsPage,
        limit: paymentsLimit,
        search: debouncedPaymentSearch || undefined,
        paymentMethod: paymentMethodFilter || undefined,
        paymentStatus: paymentStatusFilter || undefined,
        startDate: paymentDateRange.startDate || undefined,
        endDate: paymentDateRange.endDate || undefined
      });

      if (result.success) {
        return {
          payments: result.payments || [],
          pagination: result.pagination || {}
        };
      }

      throw new Error(result.error || 'Failed to fetch payment history');
    },
    enabled: activeTab === 'outstanding' && !!id,
    placeholderData: keepPreviousData
  });

  const customerPayments = paymentsData?.payments || [];
  const paymentsPagination = paymentsData?.pagination || {};

  const handlePaymentSaved = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['customerFinancials', id] }),
      queryClient.invalidateQueries({ queryKey: ['customerPayments', id] }),
      queryClient.invalidateQueries({ queryKey: ['customerKpis', id] }),
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', id] })
    ]);
  };

  const openAddPaymentModal = (invoiceId = null) => {
    setPaymentInvoiceId(invoiceId || null);
    setShowAddPaymentModal(true);
  };

  const closeAddPaymentModal = () => {
    setShowAddPaymentModal(false);
    setPaymentInvoiceId(null);
  };

  useEffect(() => {
    if (activeTab !== 'outstanding') {
      setPaymentsPage(1);
    }
  }, [activeTab]);

  // Extract transformed data
  const customer = customer360Data?.customer || null;
  const rawCustomer = customer360Data?.rawCustomer || null;
  const error = customer360Data?.error || customer360Error?.message || null;

  // Use KPIs from separate API call without customer data fallback to ensure strict real-time parity
  const kpis = kpisData || {
    totalInvoices: 0,
    totalValue: 0,
    totalReviews: 0,
    totalTickets: customer?.totalTickets ?? 0,
    openTicketsCount: customer?.openTicketsCount ?? 0,
    resolvedTicketsCount: customer?.resolvedTicketsCount ?? 0,
    hasKpiOutstanding: false,
    kpiOutstandingAmount: null,
    kpiReceivedAmount: null
  };

  // Fetch interactions when Interactions tab is active (paginated)
  const {
    data: interactionsData,
    isLoading: interactionsLoading,
    isError: interactionsError,
    isFetching: interactionsLoadingMore
  } = useQuery({
    queryKey: ['customerInteractions', id, interactionsPage],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerInteractions(id, {
        sortBy: 'performedAt',
        sortOrder: 'desc',
        limit: 10,
        page: interactionsPage
      });

      if (result.success) {
        const newInteractions = result.activities || [];
        const pagination = result.pagination || {};
        const currentPage = pagination.currentPage || interactionsPage;
        const totalPages = pagination.totalPages || (newInteractions.length === 10 ? interactionsPage + 1 : interactionsPage);

        return {
          activities: newInteractions,
          currentPage,
          totalPages,
          hasMore: currentPage < totalPages
        };
      } else {
        throw new Error(result.error || 'Failed to fetch interactions');
      }
    },
    enabled: activeTab === 'interactions' && !!id,
    placeholderData: keepPreviousData
  });

  // Reset interactions when tab changes
  useEffect(() => {
    if (activeTab !== 'interactions') {
      // Clear state when leaving interactions tab
      setInteractionsPage(1);
      setAllInteractions([]);
    } else {
      // When switching to interactions tab, reset to page 1 and clear previous data
      setInteractionsPage(1);
      setAllInteractions([]);
    }
  }, [activeTab]);

  // Accumulate interactions across pages
  useEffect(() => {
    // Only process if we're on the interactions tab
    if (activeTab !== 'interactions') {
      return;
    }

    // Only accumulate if we have data and it matches the current page
    if (interactionsData?.activities) {
      const dataPage = interactionsData.currentPage || 1;

      // Only update if the data page matches the current interactions page
      if (dataPage === interactionsPage) {
        if (interactionsPage === 1) {
          // First page - replace all (don't append)
          setAllInteractions(interactionsData.activities);
        } else {
          // Subsequent pages - append (avoid duplicates)
          setAllInteractions(prev => {
            const existingIds = new Set(prev.map(a => a._id || a.activityId || a.id));
            const newActivities = interactionsData.activities.filter(
              a => !existingIds.has(a._id || a.activityId || a.id)
            );
            return [...prev, ...newActivities];
          });
        }
      }
    } else if (!interactionsLoading && !interactionsLoadingMore) {
      // If no data and not loading, clear interactions
      setAllInteractions([]);
    }
  }, [interactionsData, interactionsPage, activeTab, interactionsLoading, interactionsLoadingMore]);

  // Extract interactions data
  const interactions = allInteractions;
  const interactionsHasMore = interactionsData?.hasMore || false;

  const loadMoreInteractions = () => {
    if (interactionsHasMore && !interactionsLoading && !interactionsLoadingMore) {
      setInteractionsPage(prev => prev + 1);
    }
  };

  // Fetch quotations when Quotations tab is active (with pagination)
  const {
    data: quotationsData,
    isLoading: quotationsLoading,
    isError: quotationsError
  } = useQuery({
    queryKey: ['customerQuotations', id, quotationsPage, quotationsLimit],
    queryFn: async () => {
      if (!id) return null;
      const result = await quotationService.getQuotationsByCustomer(id, {
        page: quotationsPage,
        limit: quotationsLimit
      });

      if (result.success) {
        // Transform quotations to match UI format
        const mappedQuotations = (result.data || []).map((quotation) => {
          return {
            id: quotation._id,
            quoteNumber: quotation.quoteNumber || quotation._id,
            title: quotation.title || 'Untitled Quotation',
            status: quotation.status,
            date: quotation.createdAt || null,
            amount: quotation.totalAmount || 0,
            itemsCount: quotation.itemsCount || 0,
            isExpired: quotation.isExpired
          };
        });

        return {
          quotations: mappedQuotations,
          pagination: result.pagination || {}
        };
      } else {
        throw new Error(result.error || 'Failed to fetch quotations');
      }
    },
    enabled: activeTab === 'quotations' && !!id,
    placeholderData: keepPreviousData
  });

  // Extract quotations data and pagination
  const customerQuotations = quotationsData?.quotations || [];
  const quotationsPagination = quotationsData?.pagination || {};

  // Reset quotations page when tab changes
  useEffect(() => {
    if (activeTab !== 'quotations') {
      setQuotationsPage(1);
    }
  }, [activeTab]);

  // Fetch invoices when Invoices tab is active (with pagination)
  const {
    data: invoicesData,
    isLoading: invoicesLoading,
    isError: invoicesError
  } = useQuery({
    queryKey: ['customerInvoices', id, invoicesPage, invoicesLimit, debouncedInvoiceSearchTerm, invoiceDateRange],
    queryFn: async () => {
      if (!id) return null;
      const result = await customerService.getCustomerInvoices(id, {
        page: invoicesPage,
        limit: invoicesLimit,
        invoiceNumber: debouncedInvoiceSearchTerm || undefined,
        startDate: invoiceDateRange.startDate || undefined,
        endDate: invoiceDateRange.endDate || undefined
      });

      if (result.success) {
        return {
          invoices: result.invoices || [],
          pagination: result.pagination || {}
        };
      }

      throw new Error(result.error || 'Failed to fetch invoices');
    },
    enabled: activeTab === 'invoices' && !!id,
    placeholderData: keepPreviousData
  });

  // Extract invoices data and pagination
  const customerInvoices = invoicesData?.invoices || [];
  const invoicesPagination = invoicesData?.pagination || {};

  const sortedCustomerInvoices = useMemo(() => {
    const list = Array.isArray(customerInvoices) ? [...customerInvoices] : [];

    const getComparableValue = (invoice) => {
      switch (invoicesSortBy) {
        case 'invoiceNumber':
          return String(invoice?.invoiceNumber || '').toLowerCase();
        case 'invoiceDate':
          return invoice?.invoiceDate ? new Date(invoice.invoiceDate).getTime() : 0;
        case 'totalAmount':
          return Number(invoice?.totalAmount ?? 0);

        default:
          return '';
      }
    };

    list.sort((first, second) => {
      const firstValue = getComparableValue(first);
      const secondValue = getComparableValue(second);

      if (firstValue < secondValue) return invoicesSortOrder === 'asc' ? -1 : 1;
      if (firstValue > secondValue) return invoicesSortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [customerInvoices, invoicesSortBy, invoicesSortOrder]);

  const handleInvoiceSort = (column) => {
    if (invoicesSortBy === column) {
      setInvoicesSortOrder((previous) => (previous === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setInvoicesSortBy(column);
    setInvoicesSortOrder(column === 'invoiceDate' ? 'desc' : 'asc');
  };

  const getInvoiceSortIndicator = (column) => {
    if (invoicesSortBy !== column) return <ArrowUpDown className="h-4 w-4 text-gray-400" />;
    return invoicesSortOrder === 'asc'
      ? <ArrowUp className="h-4 w-4" />
      : <ArrowDown className="h-4 w-4" />;
  };

  // Reset invoices page when tab changes
  useEffect(() => {
    if (activeTab !== 'invoices') {
      setInvoicesPage(1);
      setIsInvoiceModalOpen(false);
      setSelectedInvoice(null);
    }
  }, [activeTab]);

  // Reset reviews page when tab changes
  useEffect(() => {
    if (activeTab !== 'reviews') {
      setReviewsPage(1);
    }
  }, [activeTab]);

  // Fetch reviews when Reviews tab is active
  const {
    data: customerReviewsData,
    isLoading: customerReviewsLoading,
    isError: customerReviewsHasError,
    error: customerReviewsQueryError
  } = useQuery({
    queryKey: ['customerReviews', id, reviewsPage, reviewsLimit],
    queryFn: async () => {
      if (!id) return [];
      const result = await customerService.getCustomerReviews(id, {
        page: reviewsPage,
        limit: reviewsLimit
      });

      if (result.success) {
        return {
          reviews: result.reviews || [],
          pagination: result.pagination || {}
        };
      }

      throw new Error(result.error || 'Failed to fetch customer reviews');
    },
    placeholderData: keepPreviousData,
    enabled: activeTab === 'reviews' && !!id
  });

  const customerReviews = customerReviewsData?.reviews || [];
  const customerReviewsPagination = customerReviewsData?.pagination || {};

  const {
    mutate: createCustomerReview,
    isLoading: isCreatingCustomerReview
  } = useMutation({
    mutationFn: async ({ customerId, review, file }) => {
      const result = await customerService.createCustomerReview({ customerId, review, file });
      if (!result.success) {
        throw new Error(result.error || 'Failed to create customer review');
      }
      return result.review;
    },
    onSuccess: () => {
      setReviewText('');
      setReviewError('');
      setReviewFile(null);
      setReviewsPage(1);
      queryClient.invalidateQueries(['customerReviews', id]);
    },
    onError: (mutationError) => {
      setReviewError(mutationError?.message || 'Failed to create customer review');
    }
  });

  const handleSubmitCustomerReview = () => {
    const sanitizedReview = (reviewText || '').trim();

    if (!sanitizedReview) {
      setReviewError('Review is required');
      return;
    }

    if (sanitizedReview.length > CUSTOMER_REVIEW_MAX_LENGTH) {
      setReviewError(`Review must be ${CUSTOMER_REVIEW_MAX_LENGTH} characters or less`);
      return;
    }

    setReviewError('');
    createCustomerReview({ customerId: id, review: sanitizedReview, file: reviewFile });
  };

  // Helper functions for interactions display (similar to Lead 360)
  const getActivityIcon = (type) => {
    switch (type) {
      case 'call': return PhoneCall;
      case 'email': return MailIcon;
      case 'whatsapp': return MessageCircle;
      case 'meeting': return Calendar;
      case 'note': return FileText;
      default: return Clock;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'call': return 'text-blue-600';
      case 'email': return 'text-green-600';
      case 'whatsapp': return 'text-green-500';
      case 'meeting': return 'text-purple-600';
      case 'note': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  const getActivityBgColor = (type) => {
    switch (type) {
      case 'call': return 'bg-blue-100';
      case 'email': return 'bg-green-100';
      case 'whatsapp': return 'bg-green-100';
      case 'meeting': return 'bg-purple-100';
      case 'note': return 'bg-gray-100';
      default: return 'bg-gray-100';
    }
  };

  const getActivityTime = (activity) => {
    if (!activity) return new Date().toISOString();
    if (activity.activityNature === 'followup' && activity.scheduledFor) {
      return activity.scheduledFor;
    }
    return activity.performedAt || activity.createdAt || new Date().toISOString();
  };

  const getPerformerName = (performedBy) => {
    if (!performedBy) return 'Unknown';
    if (typeof performedBy === 'string') return performedBy;
    if (performedBy.firstName && performedBy.lastName) {
      return `${performedBy.firstName} ${performedBy.lastName}`;
    }
    if (performedBy.firstName) return performedBy.firstName;
    if (performedBy.email) return performedBy.email;
    return 'Unknown';
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return null;
    const totalSeconds = Math.floor(seconds);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    if (hours > 0) {
      if (mins === 0 && secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (secs === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else if (mins > 0) {
      if (secs === 0) {
        return `${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return `${mins} minute${mins !== 1 ? 's' : ''} ${secs} second${secs !== 1 ? 's' : ''}`;
      }
    } else {
      return `${secs} second${secs !== 1 ? 's' : ''}`;
    }
  };

  // Capitalize first letter of each word
  const capitalizeText = (text) => {
    if (!text) return '';
    return text
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };


  // Handler Functions
  const handleMargERPView = () => {
    const margParams = new URLSearchParams({
      customer_id: customer.id,
      gstin: customer.gstin,
      action: "view_orders",
    });
    const margERPUrl = `https://margcompusoft.com/customer?${margParams}`;
    window.open(margERPUrl, "_blank");
  };

  const handleCreateQuote = () => {
    if (customer?.id) {
      navigate(`/crm/quotation-proposal/quote-builder?customerId=${customer.id}`);
    } else {
      navigate("/crm/quotation-proposal/dashboard");
    }
  };

  // **NEW: Message Handlers**
  const handleNewMessage = () => {
    setMessageRecipient(customer?.email || "");
    setShowMessageModal(true);
  };

  const handleSendMessage = async () => {
    const requiredFields = ['messageContent'];
    if (messageType === "email") {
      requiredFields.push('messageSubject');
    }

    const formData = {
      messageContent,
      messageSubject: messageType === "email" ? messageSubject : ''
    };
    const validation = validateRequiredFields(formData, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      if (!messageContent.trim()) {
        alert("Please enter message content");
      } else if (messageType === "email" && !messageSubject.trim()) {
        alert("Please enter email subject");
      }
      return;
    }

    setErrors({});

    try {
      setIsSavingActivity(true);

      const titleMap = {
        email: messageSubject || "Email sent",
        whatsapp: "WhatsApp message sent",
        sms: "SMS sent"
      };

      const result = await customerService.createCustomerActivity(id, {
        type: messageType === "sms" ? "note" : messageType,
        title: titleMap[messageType] || "Message sent",
        description: messageType === "email"
          ? `Subject: ${messageSubject}\n${messageContent}`
          : messageContent,
        outcome: messageType === "email" ? "sent" : "completed",
        priority: "medium",
        notes: messageRecipient ? `Recipient: ${messageRecipient}` : undefined,
        performedAt: new Date().toISOString()
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to save activity");
      }

      await queryClient.invalidateQueries(['customerInteractions', id]);

      setMessageContent("");
      setMessageSubject("");
      setMessageType("whatsapp");
      setShowMessageModal(false);

      alert(
        `${messageType.charAt(0).toUpperCase() + messageType.slice(1)} activity saved successfully!`
      );
    } catch (error) {
      alert(error.message || "Failed to save activity");
    } finally {
      setIsSavingActivity(false);
    }
  };

  // **NEW: Call Log Handlers**
  const handleNewCallLog = () => {
    setShowCallLogModal(true);
  };

  const handleSaveCallLog = async () => {
    const requiredFields = ['callNotes'];
    const formData = { callNotes };
    const validation = validateRequiredFields(formData, requiredFields);

    if (!validation.isValid) {
      setErrors(validation.errors);
      alert("Please enter call notes");
      return;
    }

    setErrors({});

    try {
      setIsSavingActivity(true);

      const result = await customerService.createCustomerActivity(id, {
        type: "call",
        title: `${callDirection === "incoming" ? "Incoming" : "Outgoing"} call`,
        description: `${callOutcome ? `Outcome: ${callOutcome} - ` : ""}${callNotes}`,
        outcome: "completed",
        priority: followUpRequired ? "high" : "medium",
        duration: callDuration && !Number.isNaN(parseInt(callDuration)) ? parseInt(callDuration) : undefined,
        notes: [
          `Direction: ${callDirection}`,
          followUpRequired ? `Follow-up required${followUpDate ? ` on ${followUpDate}` : ""}` : "",
        ].filter(Boolean).join("\n"),
        performedAt: new Date().toISOString(),
        callMetadata: {
          direction: callDirection,
          outcome: callOutcome || undefined,
          followUpRequired,
          followUpDate: followUpDate || undefined
        }
      });

      if (!result.success) {
        throw new Error(result.error || "Failed to save call log");
      }

      await queryClient.invalidateQueries(['customerInteractions', id]);

      setCallDirection("outbound");
      setCallDuration("");
      setCallNotes("");
      setCallOutcome("");
      setFollowUpRequired(false);
      setFollowUpDate("");
      setShowCallLogModal(false);

      alert("Call log saved successfully!");
    } catch (error) {
      alert(error.message || "Failed to save call log");
    } finally {
      setIsSavingActivity(false);
    }
  };

  const docTypes = [
    { value: "certificate", label: "Certificate" },
    { value: "license", label: "License" },
    { value: "identity", label: "Identity Document" },
    { value: "financial", label: "Financial Document" },
    { value: "other", label: "Other" },
  ];

  const messageTypes = [
    { value: "whatsapp", label: "WhatsApp", icon: MessageCircle },
    { value: "email", label: "Email", icon: MailIcon },
    { value: "sms", label: "SMS", icon: Phone },
  ];

  const callOutcomes = [
    { value: "successful", label: "Successful" },
    { value: "no_answer", label: "No Answer" },
    { value: "busy", label: "Busy" },
    { value: "voicemail", label: "Voicemail" },
    { value: "follow_up_required", label: "Follow-up Required" },
  ];

  const tabs = [
    { id: "overview", label: "Customer Info", icon: User },
    { id: "quotations", label: "Quotations", icon: FileText },
    { id: "invoices", label: "Invoices", icon: FileText },
    { id: "outstanding", label: "Outstanding", icon: IndianRupee },
    { id: "reviews", label: "Reviews", icon: MessageSquare },
    { id: "analytics", label: "Analytics", icon: TrendingUp },
    { id: "interactions", label: "Activity", icon: MessageSquare },
    { id: "logs", label: "Logs", icon: HistoryIcon },
    { id: "documents", label: "Documents", icon: FileImage },
    { id: "assignment_history", label: "Assignment History", icon: HistoryIcon },
  ];

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case "low":
        return "bg-green-100 text-green-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case "low":
        return "bg-blue-100 text-blue-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "high":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "shipped":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading || kpisLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-agrosaf-green"></div>
      </div>
    );
  }

  // Graceful fallback if customer not loaded or error
  if (error || !customer) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-700 font-medium mb-2">
            {error || 'Customer not found.'}
          </p>
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/customer_360')}
          >
            Back to Customer 360
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto">
      {/* Header */}
      <div className="space-y-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              icon={<ArrowLeft className="h-4 w-4" />}
              onClick={() => navigate(-1)}
            >

            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer 360°</h1>
              <p className="text-gray-600">Complete customer profile, orders, interactions, and support tickets</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={handleExportCustomer}
              variant="outline"
              loading={isExporting}
              disabled={isExporting}
              className="shadow-sm whitespace-nowrap"
              icon={<Download className="h-4 w-4" />}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
            <Button
              onClick={() => setShowWhatsAppModal(true)}
              variant="primary"
              className="shadow-sm whitespace-nowrap bg-green-600 hover:bg-green-700 border-green-600"
              icon={<MessageCircle className="h-4 w-4" />}
            >
              WhatsApp
            </Button>
            <Button
              onClick={() => setShowSendEmailModal(true)}
              variant="primary"
              className="shadow-sm whitespace-nowrap"
              icon={<Send className="h-4 w-4" />}
            >
              Send Mail
            </Button>
            <PermissionGate permission="customer_management" action="update">
              <Button
                onClick={() => navigate(`/customer_360/edit/${id}`)}
                className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm whitespace-nowrap"
                icon={<Edit className="h-4 w-4" />}
              >
                Edit Customer
              </Button>
            </PermissionGate>
            <PermissionGate permission="quotation_management" action="create">
              <Button
                onClick={handleCreateQuote}
                className="bg-orange-600 hover:bg-orange-700 text-white shadow-sm whitespace-nowrap"
                icon={<Plus className="h-4 w-4" />}
              >
                Create Quote
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>

      {/* Key Metrics - Invoice KPIs + payment/outstanding summary */}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4 mb-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-blue-600">
                Total Invoice
              </p>
              <p className="text-xl font-bold text-blue-900 break-words leading-tight">
                {formatIndianNumber(kpis.totalInvoices ?? 0)}
              </p>
            </div>
            <ShoppingCart className="h-8 w-8 text-blue-600 flex-shrink-0" />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-green-600">
                Total Amount
              </p>
              <p
                className="text-xl font-bold text-green-900 break-words leading-tight"
                title={`₹${formatIndianAmount(kpis.totalValue ?? 0)}`}
              >
                ₹{formatIndianAmount(kpis.totalValue ?? 0)}
              </p>
            </div>
            <IndianRupee className="h-8 w-8 text-green-600 flex-shrink-0 font-bold text-2xl" />
          </div>
        </div>
        <div className="bg-teal-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-teal-600">
                Total Received
              </p>
              <p
                className="text-xl font-bold text-teal-900 break-words leading-tight"
                title={`₹${formatIndianAmount(
                  kpis.hasKpiOutstanding
                    ? (kpis.kpiReceivedAmount ?? 0)
                    : (financials.totalReceivedAmount ?? 0)
                )}`}
              >
                {financialsLoading || kpisLoading ? '...' : `₹${formatIndianAmount(
                  kpis.hasKpiOutstanding
                    ? (kpis.kpiReceivedAmount ?? 0)
                    : (financials.totalReceivedAmount ?? 0)
                )}`}
              </p>
            </div>
            <CheckCircle className="h-8 w-8 text-teal-600 flex-shrink-0" />
          </div>
        </div>
        <div className="bg-red-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-red-600">
                Outstanding Amount
              </p>
              <p
                className="text-xl font-bold text-red-900 break-words leading-tight"
                title={`₹${formatIndianAmount(
                  kpis.hasKpiOutstanding
                    ? (kpis.kpiOutstandingAmount ?? 0)
                    : (financials.outstandingAmount ?? 0)
                )}`}
              >
                {financialsLoading || kpisLoading ? '...' : `₹${formatIndianAmount(
                  kpis.hasKpiOutstanding
                    ? (kpis.kpiOutstandingAmount ?? 0)
                    : (financials.outstandingAmount ?? 0)
                )}`}
              </p>
            </div>
            <AlertTriangle className="h-8 w-8 text-red-600 flex-shrink-0" />
          </div>
        </div>
        <div className="bg-indigo-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-indigo-600">
                Total Reviews
              </p>
              <p className="text-xl font-bold text-indigo-900 break-words leading-tight">
                {formatIndianNumber(kpis.totalReviews ?? 0)}
              </p>
            </div>
            <MessageSquare className="h-8 w-8 text-indigo-600 flex-shrink-0" />
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-orange-600">
                Last Order
              </p>
              <p className="text-xl font-bold text-orange-900 break-words leading-tight">
                {kpis.lastInvoiceDate ? (
                  (() => {
                    const diffTime = Math.max(0, new Date() - new Date(kpis.lastInvoiceDate));
                    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                    return diffDays === 0 ? 'Today' : diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
                  })()
                ) : 'No order'}
              </p>
              {kpis.lastInvoiceDate && (
                <p className="text-xs text-orange-700 mt-1">
                  {formatDate(kpis.lastInvoiceDate, { includeTime: false })}
                </p>
              )}
            </div>
            <Calendar className="h-8 w-8 text-orange-600 flex-shrink-0" />
          </div>
        </div>
      </div>

      {/* Customer Info Header - Enhanced */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
          {/* Profile Picture/Avatar */}
          <div className="flex-shrink-0">
            {customer.profileImage ? (
              <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-4 border-white shadow-md relative group transition-transform hover:scale-105">
                <img
                  src={customer.profileImage}
                  alt={customer.name || 'Customer'}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.parentElement.querySelector('.avatar-fallback');
                    if (fallback) fallback.style.display = 'flex';
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-agrosaf-green to-agrosaf-green-hover flex items-center justify-center text-white text-2xl font-bold avatar-fallback underline-none" style={{ display: 'none' }}>
                  {(customer.name || 'Customer')
                    .split(' ')
                    .filter(Boolean)
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </div>
              </div>
            ) : (
              <div className="w-24 h-24 bg-gradient-to-br from-agrosaf-green to-agrosaf-green-hover rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-md border-4 border-white transition-transform hover:scale-105">
                {(customer.name || 'Customer')
                  .split(' ')
                  .filter(Boolean)
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
            )}
          </div>

          {/* Customer Details - Enhanced Layout */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center gap-4 mb-4">
              <div className="min-w-0 shrink-0">
                <h2 className="text-2xl font-bold text-gray-900 mb-1 capitalize">
                  {capitalizeText(customer.name)}
                </h2>
                {customer.businessName && (
                  <p className="text-base text-gray-600 capitalize flex items-center gap-2">
                    <Building className="h-4 w-4" />
                    {capitalizeText(customer.businessName)}
                  </p>
                )}
              </div>

              {/* Account Holder — compact avatar pill between name and source badges */}
              {(() => {
                const holderName = rawCustomer?.assignedTo
                  ? `${rawCustomer.assignedTo.firstName || ''} ${rawCustomer.assignedTo.lastName || ''}`.trim() || 'N/A'
                  : null;
                const managerName = rawCustomer?.assignedTo
                  ? (rawCustomer.assignedTo.managerName
                    || `${rawCustomer.assignedTo.manager?.firstName || ''} ${rawCustomer.assignedTo.manager?.lastName || ''}`.trim()
                    || '')
                  : '';
                const initials = holderName
                  ? holderName.split(/\s+/).filter(Boolean).map((n) => n[0]).join('').toUpperCase().slice(0, 2)
                  : '?';

                return (
                  <div
                    className="inline-flex items-center gap-2.5 min-w-0 max-w-full md:flex-1 md:max-w-sm md:mx-auto"
                    title={holderName ? `Account Holder: ${holderName}${managerName ? ` · Manager: ${managerName}` : ''}` : 'Unassigned'}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                      holderName
                        ? 'bg-gradient-to-br from-purple-500 to-violet-600 text-white'
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {holderName ? initials : <User className="h-3.5 w-3.5" />}
                    </div>
                    <div className="min-w-0 leading-tight">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-purple-500">
                        Account Holder
                      </p>
                      <p className={`text-sm font-semibold truncate ${holderName ? 'text-gray-900' : 'text-gray-500'}`}>
                        {holderName || 'Unassigned'}
                        {managerName ? (
                          <span className="font-normal text-gray-500"> · Mgr: {managerName}</span>
                        ) : null}
                      </p>
                    </div>
                  </div>
                );
              })()}

              {/* Badges */}
              <div className="flex items-center gap-3 shrink-0">
                {/* Data Source Badge */}
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${rawCustomer?.isFromMarg
                  ? 'bg-indigo-100 text-indigo-800 border border-indigo-200'
                  : 'bg-teal-100 text-teal-800 border border-teal-200'
                  }`}>
                  {rawCustomer?.isFromMarg ? (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <Database className="h-4 w-4 mr-1.5 " />
                      Marg ERP
                    </div>
                  ) : (
                    <div className="flex gap-1 items-center whitespace-nowrap">
                      <HardDrive className="h-4 w-4 mr-1.5 " />
                      CRM
                    </div>
                  )}
                </span>

                {/* Status Badge */}
                <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${rawCustomer?.isActive !== false
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-red-100 text-red-800 border border-red-200'
                  }`}>
                  {rawCustomer?.isActive !== false ? (
                    <>
                      <CheckCircle className="h-4 w-4 mr-1.5" />
                      Active
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 mr-1.5" />
                      Blocked
                    </>
                  )}
                </span>
              </div>
            </div>

            {/* Contact Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Email */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Mail className="h-5 w-5 text-blue-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Email</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={customer.email}
                  >
                    {customer.email || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                  <Phone className="h-5 w-5 text-green-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Phone</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={Array.isArray(customer.phone) ? customer.phone.join(', ') : customer.phone}
                  >
                    {(Array.isArray(customer.phone) ? customer.phone[0] : customer.phone) || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Customer ID */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center">
                  <Tag className="h-5 w-5 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">Customer ID</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={rawCustomer?.customerId}
                  >
                    {rawCustomer?.customerId || 'N/A'}
                  </p>
                </div>
              </div>

              {/* GST Number */}
              <div className="flex items-start space-x-3 min-w-0">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                  <FileText className="h-5 w-5 text-orange-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 mb-1">GST Number</p>
                  <p
                    className="text-sm font-medium text-gray-900 break-words"
                    title={rawCustomer?.gstNumber}
                  >
                    {rawCustomer?.gstNumber || '--'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200 overflow-x-auto overflow-y-hidden custom-scrollbar scroll-smooth">
          <nav className="-mb-px flex space-x-8 px-6 min-w-max">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${activeTab === tab.id
                    ? "border-agrosaf-green text-agrosaf-green"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Customer Info Tab */}
          {activeTab === "overview" && (
            <>
              <div className="space-y-6">
                {!rawCustomer ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">No customer information available.</p>
                  </div>
                ) : (
                  <>
                    {/* Business Information Section */}
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">Business Information</h3>
                        <PermissionGate permission="customer_management" action="update">
                          <Button
                            variant="outline"
                            size="sm"
                            icon={<Edit className="h-4 w-4" />}
                            onClick={() => navigate(`/customer_360/edit/${id}`)}
                          >
                            Edit Profile
                          </Button>
                        </PermissionGate>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Customer ID</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.customerId || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Contact Person</p>
                          <p className="text-base font-medium text-gray-900 capitalize">{rawCustomer.contactPerson || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Company Name</p>
                          <p className="text-base font-medium text-gray-900 capitalize break-all break-word">{rawCustomer.companyName || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Email</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.email || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Phone</p>
                          <p className="text-base font-medium text-gray-900">
                            {(Array.isArray(rawCustomer.phone) ? rawCustomer.phone[0] : rawCustomer.phone) || '--'}
                          </p>
                        </div>
                        {Array.isArray(rawCustomer.phone) && rawCustomer.phone.slice(1).map((p, idx) => (
                          <div key={`alt-phone-${idx}`} className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Alternative Phone {idx + 1}</p>
                            <p className="text-base font-medium text-gray-900">{p}</p>
                          </div>
                        ))}

                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">GST Number (Marg)</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.gstNumber || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">PAN Card Number (Marg)</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.pan || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Drug License Number (Marg)</p>
                          <p className="text-base font-medium text-gray-900">{rawCustomer.drugLicenseNumber || '--'}</p>
                        </div>
                        {(customer?.documents || []).map((doc, idx) => (
                          <div key={doc.id || `uploaded-doc-${idx}`} className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1 capitalize">{doc.type || 'Document'}</p>
                            <p className="text-base font-medium text-gray-900">{doc.number && doc.number !== 'N/A' ? doc.number : '--'}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Company Profile information - Internal */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Profile</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Website</p>
                          {rawCustomer.website ? (
                            <a
                              href={rawCustomer.website.startsWith('http') ? rawCustomer.website : `https://${rawCustomer.website}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-base font-medium text-blue-600 hover:text-blue-700 hover:underline break-all"
                            >
                              {rawCustomer.website}
                            </a>
                          ) : (
                            <p className="text-base font-medium text-gray-900">--</p>
                          )}
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Industry</p>
                          <p className="text-base font-medium text-gray-900 capitalize break-words">{rawCustomer.industry || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Company Size</p>
                          <p className="text-base font-medium text-gray-900 break-words">{rawCustomer.companySize || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Annual Revenue</p>
                          <p className="text-base font-medium text-gray-900">
                            {rawCustomer.annualRevenue ? `₹${Number(rawCustomer.annualRevenue).toLocaleString('en-IN')}` : '--'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Region</p>
                          <p className="text-base font-medium text-gray-900 capitalize break-words">{rawCustomer.region || '--'}</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Decision Maker</p>
                          <p className="text-base font-medium text-gray-900 break-words">{rawCustomer.decisionMaker || '--'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Marg ERP Details - NEW SECTION */}
                    {rawCustomer.isFromMarg && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Marg ERP Details</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Marg Customer ID (CID)</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margCID || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Marg Route</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margRoute || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Marg Area</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margArea || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">MR Code</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margMrCode || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Station Code (SCode)</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margSCode || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Credit Limit</p>
                            <p className="text-base font-medium text-gray-900">₹{formatIndianAmount(rawCustomer.creditLimit || 0)}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Credit Days</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditDays || 0} Days</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Credit Bills</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditBills || 0} Bills</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Credit Status</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margCreditStatus || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Marg Rate Category</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margRate || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Marg Code</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margCode || '--'}</p>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <p className="text-sm text-gray-600 mb-1">Additional field</p>
                            <p className="text-base font-medium text-gray-900">{rawCustomer.margAddField || '--'}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Addresses */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Addresses</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Billing Address */}
                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                              <MapPin className="h-4 w-4 text-blue-600" />
                            </div>
                            <p className="text-base font-semibold text-gray-900">Billing Address</p>
                          </div>
                          {rawCustomer.billingAddress ? (
                            <div className="space-y-2">
                              {(() => {
                                const streetParts = [
                                  rawCustomer.billingAddress.street,
                                  rawCustomer.billingAddress.street2,
                                  rawCustomer.billingAddress.street3
                                ].map(s => s?.trim()).filter(Boolean);

                                return streetParts.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Street</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize break-all break-word">{streetParts.join(', ')}</p>
                                  </div>
                                );
                              })()}
                              <div className="grid grid-cols-2 gap-3">
                                {rawCustomer.billingAddress.city && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">City</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.billingAddress.city === 'object' ? rawCustomer.billingAddress.city?.name : rawCustomer.billingAddress.city}
                                    </p>
                                  </div>
                                )}
                                {rawCustomer.billingAddress.state && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">State</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.billingAddress.state === 'object' ? rawCustomer.billingAddress.state?.name : rawCustomer.billingAddress.state}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                {rawCustomer.billingAddress.pincode && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                                    <p className="text-sm font-medium text-gray-900">{rawCustomer.billingAddress.pincode}</p>
                                  </div>
                                )}
                                {rawCustomer.billingAddress.country && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Country</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.billingAddress.country === 'object' ? rawCustomer.billingAddress.country?.name : rawCustomer.billingAddress.country}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Not provided</p>
                          )}
                        </div>

                        {/* Shipping Address */}
                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                          <div className="flex items-center gap-2 mb-4">
                            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                              <Truck className="h-4 w-4 text-green-600" />
                            </div>
                            <p className="text-base font-semibold text-gray-900">Shipping Address</p>
                          </div>
                          {rawCustomer.shippingAddress ? (
                            <div className="space-y-2">
                              {(() => {
                                const streetParts = [
                                  rawCustomer.shippingAddress.street,
                                  rawCustomer.shippingAddress.street2,
                                  rawCustomer.shippingAddress.street3
                                ].map(s => s?.trim()).filter(Boolean);

                                return streetParts.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Street</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize break-all break-word">{streetParts.join(', ')}</p>
                                  </div>
                                );
                              })()}
                              <div className="grid grid-cols-2 gap-3">
                                {rawCustomer.shippingAddress.city && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">City</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.shippingAddress.city === 'object' ? rawCustomer.shippingAddress.city?.name : rawCustomer.shippingAddress.city}
                                    </p>
                                  </div>
                                )}
                                {rawCustomer.shippingAddress.state && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">State</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.shippingAddress.state === 'object' ? rawCustomer.shippingAddress.state?.name : rawCustomer.shippingAddress.state}
                                    </p>
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                {rawCustomer.shippingAddress.pincode && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Pincode</p>
                                    <p className="text-sm font-medium text-gray-900">{rawCustomer.shippingAddress.pincode}</p>
                                  </div>
                                )}
                                {rawCustomer.shippingAddress.country && (
                                  <div>
                                    <p className="text-xs text-gray-500 mb-0.5">Country</p>
                                    <p className="text-sm font-medium text-gray-900 capitalize">
                                      {typeof rawCustomer.shippingAddress.country === 'object' ? rawCustomer.shippingAddress.country?.name : rawCustomer.shippingAddress.country}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <p className="text-sm text-gray-500">Not provided</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Additional Information */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Information</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Created At</p>
                          <p className="text-base font-medium text-gray-900">
                            {rawCustomer.createdAt ? formatDate(rawCustomer.createdAt, { includeTime: true }) : 'N/A'}
                          </p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <p className="text-sm text-gray-600 mb-1">Last Updated</p>
                          <p className="text-base font-medium text-gray-900">
                            {rawCustomer.updatedAt ? formatDate(rawCustomer.updatedAt, { includeTime: true }) : 'N/A'}
                          </p>
                        </div>
                        {Array.isArray(rawCustomer.tags) && rawCustomer.tags.length > 0 && (
                          <div className="bg-gray-50 rounded-lg p-4 md:col-span-2 lg:col-span-1">
                            <p className="text-sm text-gray-600 mb-2">Tags</p>
                            <div className="flex flex-wrap gap-2">
                              {rawCustomer.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Created By Details */}
                    {rawCustomer.createdBy && typeof rawCustomer.createdBy === 'object' && (
                      <div className="mt-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Created By Information</h3>
                        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {(rawCustomer.createdBy.firstName || rawCustomer.createdBy.lastName) && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Name</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {`${rawCustomer.createdBy.firstName || ''} ${rawCustomer.createdBy.lastName || ''}`.trim()}
                                </p>
                              </div>
                            )}
                            {rawCustomer.createdBy.employeeId && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Employee ID</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {rawCustomer.createdBy.employeeId}
                                </p>
                              </div>
                            )}
                            {rawCustomer.createdBy.email && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Email</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {rawCustomer.createdBy.email}
                                </p>
                              </div>
                            )}
                            {rawCustomer.createdBy.role && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Role</p>
                                <p className="text-sm font-semibold text-gray-900 uppercase">
                                  {rawCustomer.createdBy.role.replace(/_/g, ' ')}
                                </p>
                              </div>
                            )}
                            {rawCustomer.createdBy.department && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Department</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {rawCustomer.createdBy.department}
                                </p>
                              </div>
                            )}
                            {rawCustomer.createdAt && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Created At</p>
                                <p className="text-sm font-semibold text-gray-900">
                                  {formatDate(rawCustomer.createdAt, { includeTime: true })}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Associated Lead Section */}
                    {customer?.createdFromLead && (
                      <div className="space-y-6 mb-6">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Lead Association</h3>
                          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-lg p-5 border border-teal-100 shadow-sm">
                            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                              <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center border border-teal-200">
                                  <User className="h-6 w-6 text-teal-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-teal-900">
                                    {customer.createdFromLead.firstName} {customer.createdFromLead.lastName || ''}
                                  </p>
                                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-teal-700">
                                    {customer.createdFromLead.leadId && (
                                      <span className="flex items-center gap-1">
                                        <Tag className="h-3.5 w-3.5 text-teal-600" />
                                        <strong>Lead ID:</strong> {customer.createdFromLead.leadId}
                                      </span>
                                    )}
                                    {customer.createdFromLead.company && (
                                      <span className="flex items-center gap-1">
                                        <Building className="h-3.5 w-3.5 text-teal-600" />
                                        <strong>Company:</strong> {customer.createdFromLead.company}
                                      </span>
                                    )}
                                    {customer.createdFromLead.email && (
                                      <span className="flex items-center gap-1">
                                        <Mail className="h-3.5 w-3.5 text-teal-600" />
                                        <strong>Email:</strong> {customer.createdFromLead.email}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <Button
                                onClick={() => navigate(`/lead_detail_360/${customer.createdFromLead._id || customer.createdFromLead.id}`)}
                                className="bg-teal-600 hover:bg-teal-700 text-white shadow-sm flex items-center gap-1.5 py-2 px-4 rounded-lg font-medium text-sm transition-colors"
                                icon={<ExternalLink className="h-4 w-4" />}
                              >
                                Open Lead Details
                              </Button>
                            </div>
                          </div>
                        </div>

                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversion Details</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-100 shadow-sm flex items-start gap-3">
                              <div className="p-2 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-700 flex-shrink-0">
                                <Calendar className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="text-xs text-emerald-800 font-semibold mb-0.5">Lead Created</p>
                                <p className="text-sm font-bold text-emerald-950">
                                  {customer.createdFromLead.createdAt ? formatDate(customer.createdFromLead.createdAt, { includeTime: true }) : 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100 shadow-sm flex items-start gap-3">
                              <div className="p-2 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 flex-shrink-0">
                                <Calendar className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="text-xs text-indigo-800 font-semibold mb-0.5">Customer Created</p>
                                <p className="text-sm font-bold text-indigo-950">
                                  {customer.createdAt ? formatDate(customer.createdAt, { includeTime: true }) : 'N/A'}
                                </p>
                              </div>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-4 border border-amber-100 shadow-sm flex items-start gap-3">
                              <div className="p-2 rounded-full bg-amber-100 border border-amber-200 text-amber-700 flex-shrink-0">
                                <Clock className="h-5 w-5" />
                              </div>
                              <div>
                                <p className="text-xs text-amber-800 font-semibold mb-0.5">Conversion Time</p>
                                <p className="text-sm font-bold text-amber-950 font-sans">
                                  {(() => {
                                    if (!customer.createdAt || !customer.createdFromLead?.createdAt) return 'N/A';
                                    const leadDate = new Date(customer.createdFromLead.createdAt);
                                    const customerDate = new Date(customer.createdAt);
                                    
                                    // Normalize dates to midnight to calculate calendar day difference
                                    leadDate.setHours(0, 0, 0, 0);
                                    customerDate.setHours(0, 0, 0, 0);
                                    
                                    const diffTime = customerDate - leadDate;
                                    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
                                    return diffDays === 0 ? 'Same day' : `${diffDays} ${diffDays === 1 ? 'day' : 'days'}`;
                                  })()}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Documents Section */}
                    {Array.isArray(rawCustomer.customerDocuments) && rawCustomer.customerDocuments.length > 0 && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Documents</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200 border rounded-lg">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Expiry</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">File/Link</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-100">
                              {rawCustomer.customerDocuments.map((doc, idx) => (
                                <tr key={idx}>
                                  <td className="px-4 py-2 text-sm text-gray-900">
                                    {doc.documentTypeName || (typeof doc.documentTypeId === 'object' ? doc.documentTypeId.name : (doc.documentTypeId || '-'))}
                                  </td>
                                  <td className="px-4 py-2 text-sm text-gray-900">{doc.certificateNumber || '-'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-900">{doc.expiryDate ? (new Date(doc.expiryDate).toLocaleDateString()) : '-'}</td>
                                  <td className="px-4 py-2 text-sm text-gray-900 capitalize">{doc.documentMethod || '-'}</td>
                                  <td className="px-4 py-2 text-sm">
                                    {doc.documentMethod === 'link' && doc.documentLink ? (
                                      <a href={doc.documentLink} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">View Link</a>
                                    ) : doc.fileInfo && doc.fileInfo.url ? (
                                      <a href={doc.fileInfo.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline break-all">{doc.fileInfo.originalName || 'Download'}</a>
                                    ) : (
                                      <span className="text-gray-400">-</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* Outstanding Tab */}
          {activeTab === "outstanding" && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Payment History</h3>
                  <p className="text-sm text-gray-500">
                    Outstanding: ₹{formatIndianAmount(financials.outstandingAmount ?? 0)} · Received: ₹{formatIndianAmount(financials.totalReceivedAmount ?? 0)}
                  </p>
                </div>
                <PermissionGate permission="customer_payment" action="create">
                  <Button
                    onClick={() => openAddPaymentModal()}
                    className="bg-agrosaf-green hover:bg-agrosaf-green-hover text-white shadow-sm"
                    icon={<Plus className="h-4 w-4" />}
                  >
                    Add Payment
                  </Button>
                </PermissionGate>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[220px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    value={paymentSearch}
                    onChange={(e) => setPaymentSearch(e.target.value)}
                    placeholder="Search Payment ID or Reference"
                    className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
                  />
                </div>
                <select
                  value={paymentMethodFilter}
                  onChange={(e) => {
                    setPaymentMethodFilter(e.target.value);
                    setPaymentsPage(1);
                  }}
                  className="w-auto shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
                >
                  {PAYMENT_METHOD_OPTIONS.map((option) => (
                    <option key={option.value || 'all-methods'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  value={paymentStatusFilter}
                  onChange={(e) => {
                    setPaymentStatusFilter(e.target.value);
                    setPaymentsPage(1);
                  }}
                  className="w-auto shrink-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
                >
                  {PAYMENT_STATUS_OPTIONS.map((option) => (
                    <option key={option.value || 'all-statuses'} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="shrink-0">
                  <DateRangeFilter
                    startDate={paymentDateRange.startDate}
                    endDate={paymentDateRange.endDate}
                    onChange={(newRange) => {
                      setPaymentDateRange(newRange);
                      setPaymentsPage(1);
                    }}
                    allowFutureDates={false}
                  />
                </div>
                {(paymentSearch || paymentMethodFilter || paymentStatusFilter || paymentDateRange.startDate || paymentDateRange.endDate) && (
                  <Button
                    variant="ghost"
                    className="shrink-0 text-gray-500 hover:text-red-500 hover:bg-red-50 px-2 h-auto py-2 border border-gray-300 rounded-lg"
                    onClick={() => {
                      setPaymentSearch('');
                      setDebouncedPaymentSearch('');
                      setPaymentMethodFilter('');
                      setPaymentStatusFilter('');
                      setPaymentDateRange({ startDate: '', endDate: '' });
                      setPaymentsPage(1);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              {paymentsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading payment history...</p>
                  </div>
                </div>
              ) : paymentsError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Failed to load payment history</p>
                  <p className="text-sm text-gray-500">{paymentsQueryError?.message || 'An error occurred'}</p>
                </div>
              ) : customerPayments.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <IndianRupee className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-600 font-medium">No payments recorded yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    {canCreatePayment
                      ? 'Use Add Payment to record the first payment for this customer.'
                      : 'You have read-only access to payment history.'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment ID</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Received</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Outstanding</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {customerPayments.map((payment) => (
                          <tr key={payment._id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.paymentNumber || '--'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                              {payment.invoiceNumber || '--'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              ₹{formatIndianAmount(payment.invoiceAmount || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-semibold text-teal-700">
                              ₹{formatIndianAmount(payment.amountReceived || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-red-700">
                              ₹{formatIndianAmount(payment.outstandingAfterPayment || 0)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                              {formatPaymentMethodLabel(payment.paymentMethod)}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                              {payment.paymentDate
                                ? formatDate(payment.paymentDate, { includeTime: true })
                                : '--'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPaymentStatusClasses(payment.paymentStatus)}`}>
                                {payment.paymentStatus || '--'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setIsPaymentModalOpen(true);
                                }}
                                title="View Details"
                                aria-label="View payment details"
                                className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {(() => {
                    const totalItems = paymentsPagination.total || 0;
                    const effectiveLimit = paymentsPagination.limit || paymentsLimit || 10;
                    const totalPages = paymentsPagination.totalPages || Math.ceil(totalItems / effectiveLimit) || 1;
                    if (totalItems <= 0) return null;
                    return (
                      <PaginationControls
                        currentPage={paymentsPagination.page || paymentsPage || 1}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        limit={effectiveLimit}
                        hasNextPage={(paymentsPagination.page || paymentsPage || 1) < totalPages}
                        hasPrevPage={(paymentsPagination.page || paymentsPage || 1) > 1}
                        onPageChange={(page) => {
                          setPaymentsPage(page);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        onLimitChange={(limit) => {
                          setPaymentsLimit(limit);
                          setPaymentsPage(1);
                        }}
                      />
                    );
                  })()}
                </>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === "invoices" && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h3 className="text-lg font-semibold text-gray-900 shrink-0">
                  All Invoices
                </h3>

                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto flex-1 justify-end">
                  {/* Search Bar */}
                  <div className="relative w-full sm:max-w-[200px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search Invoice..."
                      value={invoiceSearchTerm}
                      onChange={(e) => {
                        setInvoiceSearchTerm(e.target.value);
                      }}
                      className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green focus:border-transparent w-full"
                    />
                  </div>

                  {/* Date Range Filter */}
                  <div className="w-full sm:w-auto shrinks-0">
                    <DateRangeFilter
                      startDate={invoiceDateRange.startDate}
                      endDate={invoiceDateRange.endDate}
                      onChange={(newRange) => {
                        setInvoiceDateRange(newRange);
                        setInvoicesPage(1);
                      }}
                      allowFutureDates={false}
                    />
                  </div>

                  {/* Clear Button */}
                  {(invoiceSearchTerm || invoiceDateRange.startDate || invoiceDateRange.endDate) && (
                    <Button
                      variant="ghost"
                      className="text-gray-500 hover:text-red-500 hover:bg-red-50 px-2 h-auto py-2 shrink-0 border border-gray-300 rounded-lg"
                      onClick={() => {
                        setInvoiceSearchTerm('');
                        setDebouncedInvoiceSearchTerm('');
                        setInvoiceDateRange({ startDate: '', endDate: '' });
                        setInvoicesPage(1);
                      }}
                    >
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {invoicesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading invoices...</p>
                  </div>
                </div>
              ) : invoicesError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Failed to load invoices</p>
                  <p className="text-sm text-gray-500">{invoicesError.message || 'An error occurred'}</p>
                </div>
              ) : customerInvoices.length === 0 ? (
                <p className="text-sm text-gray-500">No invoices found</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleInvoiceSort('invoiceNumber')}
                            className="inline-flex items-center gap-1 hover:text-gray-700"
                          >
                            Invoice Number
                            {getInvoiceSortIndicator('invoiceNumber')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleInvoiceSort('invoiceDate')}
                            className="inline-flex items-center gap-1 hover:text-gray-700"
                          >
                            Date
                            {getInvoiceSortIndicator('invoiceDate')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          <button
                            type="button"
                            onClick={() => handleInvoiceSort('totalAmount')}
                            className="inline-flex items-center gap-1 hover:text-gray-700"
                          >
                            Total Amount
                            {getInvoiceSortIndicator('totalAmount')}
                          </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Paid Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Outstanding Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Update Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {sortedCustomerInvoices.map((invoice) => {
                        const invoiceKey = invoice._id || `${invoice.margCustomerId}-${invoice.invoiceNumber}`;

                        return (
                          <tr key={invoiceKey} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 uppercase">
                              {invoice.invoiceNumber || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {invoice.invoiceDate ? formatDate(invoice.invoiceDate, { includeTime: false }) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              ₹{formatIndianAmount(invoice.totalAmount ?? 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-teal-700">
                              ₹{formatIndianAmount(invoice.paidAmount ?? 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-red-700">
                              ₹{formatIndianAmount(invoice.outstandingAmount ?? 0)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {invoice.lastUpdateDate
                                ? formatDate(invoice.lastUpdateDate, { includeTime: true })
                                : '--'}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                              <div className="inline-flex items-center gap-2">
                              <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedInvoice(invoice);
                                    setIsInvoiceModalOpen(true);
                                  }}
                                  title="View Details"
                                  aria-label="View Details"
                                  className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <PermissionGate permission="customer_payment" action="create">
                                  <button
                                    type="button"
                                    onClick={() => openAddPaymentModal(invoice._id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-agrosaf-green hover:bg-agrosaf-green-hover rounded-lg shadow-sm transition-colors"
                                  >
                                    <IndianRupee className="h-3.5 w-3.5" />
                                    Payment
                                  </button>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {(() => {
                const invoicesTotalItems = Number(invoicesPagination?.total || customerInvoices.length || 0);
                const effectivePaginationLimit = Number(invoicesPagination?.limit || invoicesLimit || 10);
                const invoicesTotalPages = Number(
                  invoicesPagination?.totalPages || invoicesPagination?.pages || Math.ceil(invoicesTotalItems / effectivePaginationLimit) || 1
                );
                const currentPage = Number(invoicesPage || invoicesPagination?.page || 1);
                if (invoicesLoading || invoicesTotalItems <= 0) return null;

                return (
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={invoicesTotalPages}
                    totalItems={invoicesTotalItems}
                    limit={effectivePaginationLimit}
                    hasNextPage={currentPage < invoicesTotalPages}
                    hasPrevPage={currentPage > 1}
                    onPageChange={(page) => {
                      setInvoicesPage(Number(page));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    onLimitChange={(limit) => {
                      setInvoicesLimit(Number(limit));
                      setInvoicesPage(1);
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                  />
                );
              })()}
            </div>
          )}

          {/* Quotations Tab */}
          {activeTab === "quotations" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  All Quotations
                </h3>
              </div>

              {quotationsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading quotations...</p>
                  </div>
                </div>
              ) : quotationsError ? (
                <div className="text-center py-12">
                  <p className="text-red-600 mb-2">Failed to load quotations</p>
                  <p className="text-sm text-gray-500">{quotationsError.message || 'An error occurred'}</p>
                </div>
              ) : customerQuotations.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No quotations available for this customer.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quote Number
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Items
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {customerQuotations.map((quotation) => (
                        <tr key={quotation.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {quotation.quoteNumber}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.title}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.date ? formatDate(quotation.date, { includeTime: false }) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            ₹{formatIndianAmount(quotation.amount ?? 0)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {quotation.itemsCount}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col">
                              <span className={`inline-flex items-center mr-auto px-2.5 py-0.5 rounded-full text-xs font-medium ${['order', 'ordered'].includes(quotation.status?.toLowerCase())
                                ? getQuotationStatusClasses('accepted')
                                : getQuotationStatusClasses(quotation.status)
                                }`}>
                                {['order', 'ordered'].includes(quotation.status?.toLowerCase()) ? 'Accepted' : formatQuotationStatus(quotation.status)}
                              </span>
                              {quotation.status?.toLowerCase() === 'order' && (
                                <span className="text-[12px] text-gray-500 mt-1 ml-1 italic font-medium">(Ready to order)</span>
                              )}
                              {quotation.status?.toLowerCase() === 'ordered' && (
                                <span className="text-[12px] text-gray-500 mt-1 ml-1 italic font-medium">(Ordered)</span>
                              )}
                              {quotation.isExpired && (
                                <span className="text-[12px] text-red-600 mt-1 ml-1 italic font-medium">(Expired)</span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Button
                              variant="outline"
                              size="sm"
                              icon={<Eye className="h-4 w-4" />}
                              onClick={() => navigate(`/crm/quotation-proposal/send-track/${quotation.id}?page=quote`)}
                              title="View Quotation"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {(() => {
                const quotationsTotalItems = Number(quotationsPagination?.total || customerQuotations.length || 0);
                const quotationsTotalPages = Number(quotationsPagination?.totalPages || 1);
                const effectivePaginationLimit = quotationsPagination?.limit || quotationsLimit || 10;
                // Show pagination if there's more than one page
                const shouldShowPagination = !quotationsLoading && quotationsTotalPages > 1;
                const handleQuotationsPageChange = (page) => {
                  setQuotationsPage(page);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                };

                return shouldShowPagination ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                      {/* Results Count and Limit Selector */}
                      <div className="flex items-center space-x-4">
                        <div className="text-sm text-gray-700">
                          Showing {((quotationsPagination.page || 1) - 1) * effectivePaginationLimit + 1} to {Math.min((quotationsPagination.page || 1) * effectivePaginationLimit, quotationsTotalItems)} of {quotationsTotalItems} results
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="text-sm text-gray-700">Items per page:</label>
                          <select
                            value={quotationsLimit}
                            onChange={(e) => {
                              setQuotationsLimit(Number(e.target.value));
                              setQuotationsPage(1);
                            }}
                            className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                          >
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                          </select>
                        </div>
                      </div>

                      {/* Pagination Controls */}
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleQuotationsPageChange((quotationsPagination.page || 1) - 1)}
                          disabled={(quotationsPagination.page || 1) <= 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Previous
                        </button>

                        {/* Page Numbers */}
                        <div className="flex items-center space-x-1">
                          {(() => {
                            const totalPages = quotationsPagination.totalPages || 1;
                            const currentPageNum = quotationsPagination.page || 1;
                            const pages = [];

                            if (totalPages <= 10) {
                              for (let i = 1; i <= totalPages; i++) {
                                pages.push(i);
                              }
                            } else {
                              pages.push(1);

                              if (currentPageNum <= 4) {
                                for (let i = 2; i <= 5; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              } else if (currentPageNum >= totalPages - 3) {
                                pages.push('ellipsis');
                                for (let i = totalPages - 4; i <= totalPages; i++) {
                                  pages.push(i);
                                }
                              } else {
                                pages.push('ellipsis');
                                for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                                  pages.push(i);
                                }
                                pages.push('ellipsis');
                                pages.push(totalPages);
                              }
                            }

                            return pages.map((page, index) => {
                              if (page === 'ellipsis') {
                                return (
                                  <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                                    ...
                                  </span>
                                );
                              }

                              return (
                                <button
                                  key={page}
                                  onClick={() => handleQuotationsPageChange(page)}
                                  className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                                    ? 'bg-agrosaf-green text-white'
                                    : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                  {page}
                                </button>
                              );
                            });
                          })()}
                        </div>

                        <button
                          onClick={() => handleQuotationsPageChange((quotationsPagination.page || 1) + 1)}
                          disabled={(quotationsPagination.page || 1) >= (quotationsPagination.totalPages || 1)}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          )}

          {/* Interactions Tab - Timeline format (similar to Lead 360) */}
          {activeTab === "interactions" && (
            <div className="space-y-4 sm:space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">Activity Timeline</h3>
              </div>

              {interactionsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading interactions...</p>
                  </div>
                </div>
              ) : interactionsError ? (
                <div className="text-center py-12">
                  <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
                  <p className="text-red-600 mb-2">Failed to load interactions</p>
                  <p className="text-sm text-gray-500">{interactionsError.message || 'An error occurred'}</p>
                </div>
              ) : interactions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No interactions logged yet</p>
                  <p className="text-sm text-gray-500 mt-2">Interactions will appear here once logged</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-6">
                    {interactions.map((activity) => {
                      const ActivityIcon = getActivityIcon(activity.type);
                      const iconColor = getActivityColor(activity.type);
                      const bgColor = getActivityBgColor(activity.type);

                      return (
                        <div
                          key={activity._id || activity.activityId}
                          className="relative pl-12 cursor-pointer"
                          onClick={() => {
                            const activityId = activity._id || activity.activityId || activity.id;
                            if (activityId) {
                              navigate(`/customer_activity_detail/${activityId}`);
                            }
                          }}
                        >
                          {/* Timeline dot */}
                          <div className={`absolute left-0 top-1 w-8 h-8 rounded-full ${bgColor} flex items-center justify-center border-2 border-white shadow-sm`}>
                            <ActivityIcon className={`h-4 w-4 ${iconColor}`} />
                          </div>

                          {/* Activity card */}
                          <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2">
                                  <h4 className={`text-base sm:text-lg font-semibold text-gray-900 ${activity.type === 'email' ? 'truncate' : ''
                                    }`}>
                                    {capitalizeText(activity.title || '')}
                                  </h4>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.type === 'call' ? 'bg-blue-100 text-blue-800' :
                                    activity.type === 'email' ? 'bg-green-100 text-green-800' :
                                      activity.type === 'whatsapp' ? 'bg-green-100 text-green-800' :
                                        activity.type === 'meeting' ? 'bg-purple-100 text-purple-800' :
                                          'bg-gray-100 text-gray-800'
                                    }`}>
                                    {capitalizeText(activity.type || '')}
                                  </span>
                                  {activity.isAutomated && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                      Auto
                                    </span>
                                  )}
                                </div>

                                {activity.description && (
                                  <p className={`text-sm text-gray-600 mb-3 break-words ${activity.type === 'email' ? 'line-clamp-2' : 'whitespace-pre-wrap'
                                    }`}>
                                    {activity.description}
                                  </p>
                                )}

                                <div className="flex flex-wrap items-center gap-4 text-xs sm:text-sm text-gray-500">
                                  <div className="flex items-center gap-1" title="Created Date">
                                    <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                                    <span>Created: {formatDate(activity.createdAt || activity.performedAt, { includeTime: true })}</span>
                                  </div>

                                  {activity.callMetadata?.talkDuration && activity.callMetadata.talkDuration > 0 && (
                                    <div className="flex items-center gap-1">
                                      <span>Duration: {formatDuration(activity.callMetadata.talkDuration)}</span>
                                    </div>
                                  )}

                                  {activity.performedBy && (
                                    <div className="flex items-center gap-1">
                                      <User className="h-3 w-3 sm:h-4 sm:w-4" />
                                      <span>{getPerformerName(activity.performedBy)}</span>
                                    </div>
                                  )}

                                  {activity.outcome && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${activity.outcome === 'completed' ? 'bg-green-100 text-green-800' :
                                      activity.outcome === 'sent' ? 'bg-green-100 text-green-800' :
                                        activity.outcome === 'scheduled' ? 'bg-blue-100 text-blue-800' :
                                          activity.outcome === 'cancelled' ? 'bg-red-100 text-red-800' :
                                            'bg-gray-100 text-gray-800'
                                      }`}>
                                      {capitalizeText(activity.outcome)}
                                    </span>
                                  )}
                                </div>

                                <div className="mt-3 pt-3 border-t border-gray-200">
                                  <div className="flex items-center gap-2 text-sm text-blue-600">
                                    <Calendar className="h-4 w-4" />
                                    <span>Activity Date & Time: {formatDate(getActivityTime(activity), { includeTime: true })}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {interactionsHasMore && (
                      <div className="flex justify-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMoreInteractions}
                          disabled={interactionsLoadingMore}
                        >
                          {interactionsLoadingMore ? 'Loading...' : 'Load More'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "reviews" && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 sm:p-5">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Add Review</h3>
                <div className="space-y-3">
                  <div>
                    <textarea
                      rows={4}
                      value={reviewText}
                      onChange={(e) => {
                        const sanitized = sanitizeDescriptionInput(e.target.value).slice(0, CUSTOMER_REVIEW_MAX_LENGTH);
                        setReviewText(sanitized);
                        if (reviewError) {
                          setReviewError('');
                        }
                      }}
                      placeholder="Write your review for this customer..."
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green ${reviewError ? 'border-red-300' : 'border-gray-300'
                        }`}
                      maxLength={CUSTOMER_REVIEW_MAX_LENGTH}
                    />
                    <div className="mt-1 flex justify-end text-xs text-gray-500">
                      {getCharacterCount(reviewText, CUSTOMER_REVIEW_MAX_LENGTH)}
                    </div>
                  </div>

                  {/* File Attachment Input / Preview */}
                  <div className="mt-1">
                    {isUploadingReviewFile ? (
                      <div className="flex items-center space-x-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm max-w-md animate-pulse">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-agrosaf-green flex-shrink-0"></div>
                        <span className="text-xs font-semibold text-gray-600">Processing attachment in cloud...</span>
                      </div>
                    ) : !reviewFile ? (
                      <div className="flex items-center space-x-2">
                        <label className="flex items-center space-x-2 px-3 py-1.5 bg-white border border-gray-300 rounded-lg shadow-sm cursor-pointer hover:bg-gray-50 transition duration-150 ease-in-out text-sm text-gray-700">
                          <Paperclip className="h-4 w-4 text-gray-500" />
                          <span>Attach Image or PDF</span>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (file) {
                                if (file.size > 10 * 1024 * 1024) {
                                  toast.error('File size must be less than 10MB');
                                  return;
                                }
                                setIsUploadingReviewFile(true);
                                try {
                                  const uploadFormData = new FormData();
                                  uploadFormData.append('file', file);
                                  uploadFormData.append('folder', 'customer-reviews-attachments');

                                  const uploadRes = await post(API_ENDPOINTS.UPLOAD.CLOUDINARY, uploadFormData);
                                  if (uploadRes && uploadRes.responseCode === 200 && uploadRes.data) {
                                    const metadata = {
                                      name: file.name,
                                      url: uploadRes.data.url,
                                      public_id: uploadRes.data.public_id,
                                      size: file.size,
                                      mimetype: file.type,
                                      resource_type: uploadRes.data.resource_type || (file.type.startsWith('image/') ? 'image' : 'raw')
                                    };
                                    setReviewFile(metadata);
                                    toast.success('File uploaded successfully');
                                  } else {
                                    throw new Error('Upload failed');
                                  }
                                } catch (err) {
                                  toast.error(err.message || 'File upload failed');
                                } finally {
                                  setIsUploadingReviewFile(false);
                                }
                              }
                            }}
                          />
                        </label>
                        <span className="text-xs text-gray-400">Max 10MB (PDF or Image)</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm max-w-md">
                        <div className="flex items-center space-x-3 overflow-hidden flex-1">
                          {reviewFile.mimetype && reviewFile.mimetype.startsWith('image/') ? (
                            <a
                              href={reviewFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-10 w-10 rounded bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-200 hover:opacity-90 transition block"
                            >
                              <img
                                src={reviewFile.url}
                                alt="preview"
                                className="h-full w-full object-cover cursor-zoom-in"
                              />
                            </a>
                          ) : (
                            <a
                              href={reviewFile.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-10 w-10 rounded bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100 hover:bg-red-100 transition block"
                            >
                              <FileText className="h-5 w-5 text-red-500" />
                            </a>
                          )}
                          <div className="overflow-hidden flex-1">
                            <p className="text-xs font-semibold text-gray-800 truncate" title={reviewFile.name}>{reviewFile.name}</p>
                            <div className="flex items-center space-x-2">
                              <span className="text-[10px] text-gray-400">{(reviewFile.size / 1024 / 1024).toFixed(2)} MB</span>
                              <span className="text-[10px] text-gray-300">•</span>
                              <a
                                href={reviewFile.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[10px] text-agrosaf-green hover:underline font-semibold"
                              >
                                {reviewFile.mimetype && reviewFile.mimetype.startsWith('image/') ? 'View' : 'Download'}
                              </a>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async () => {
                            if (!reviewFile?.public_id) {
                              setReviewFile(null);
                              return;
                            }
                            setIsUploadingReviewFile(true);
                            try {
                              const publicId = reviewFile.public_id;
                              const resourceType = reviewFile.resource_type || 'raw';
                              const deleteRes = await del(`${API_ENDPOINTS.UPLOAD.DELETE}?public_id=${publicId}&resource_type=${resourceType}`);
                              if (deleteRes && (deleteRes.responseCode === 200 || deleteRes.success)) {
                                setReviewFile(null);
                                toast.success('File removed from cloud');
                              } else {
                                throw new Error('Failed to delete file from cloud');
                              }
                            } catch (err) {
                              toast.error(err.message || 'File deletion failed');
                            } finally {
                              setIsUploadingReviewFile(false);
                            }
                          }}
                          className="p-1 hover:bg-gray-100 rounded-full transition text-gray-500 hover:text-red-500 ml-2"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {reviewError && (
                    <p className="text-sm text-red-600">{reviewError}</p>
                  )}

                  <div className="flex justify-end">
                    <Button
                      onClick={handleSubmitCustomerReview}
                      disabled={isCreatingCustomerReview || isUploadingReviewFile || !reviewText.trim()}
                      variant="primary"
                      icon={<Plus className="h-4 w-4" />}
                    >
                      {isCreatingCustomerReview ? 'Submitting...' : 'Submit Review'}
                    </Button>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Review History</h3>

                {customerReviewsLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-agrosaf-green mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading reviews...</p>
                    </div>
                  </div>
                ) : customerReviewsHasError ? (
                  <div className="text-center py-10">
                    <p className="text-red-600 mb-2">Failed to load reviews</p>
                    <p className="text-sm text-gray-500">{customerReviewsQueryError?.message || 'An error occurred'}</p>
                  </div>
                ) : customerReviews.length === 0 ? (
                  <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                    <MessageSquare className="h-10 w-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No reviews added yet.</p>
                  </div>
                ) : (
                  <>
                    <div className="space-y-4">
                      {customerReviews.map((review) => {
                        const salesPerson = review.salesPersonId || {};
                        const reviewerName = `${salesPerson.firstName || ''} ${salesPerson.lastName || ''}`.trim() || 'Unknown User';

                        return (
                          <div
                            key={review._id}
                            className="bg-white border border-gray-200 rounded-lg p-4 sm:p-5"
                          >
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                              <div className="flex items-center gap-4">
                                <p className="text-sm font-semibold text-gray-900">{reviewerName}</p>
                                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-1">
                                  {salesPerson.email && (
                                    <span>{salesPerson.email}</span>
                                  )}
                                  {salesPerson.role && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 font-medium capitalize">
                                      {capitalizeText(String(salesPerson.role).replace('_', ' '))}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <p className="text-xs text-gray-500 whitespace-nowrap">
                                {review.createdAt ? formatDate(review.createdAt, { includeTime: true }) : 'N/A'}
                              </p>
                            </div>

                            <p className="text-sm text-gray-700 whitespace-pre-wrap break-words first-letter:capitalize">
                              {renderReviewText(review.review)}
                            </p>
                            {review.file && review.file.url && (
                              <div className="mt-3 flex items-start">
                                {review.file.mimetype && review.file.mimetype.startsWith('image/') ? (
                                  <div className="relative group rounded-lg overflow-hidden border border-gray-200 bg-gray-50 max-w-xs shadow-sm hover:shadow transition duration-200">
                                    <a href={review.file.url} target="_blank" rel="noopener noreferrer">
                                      <img
                                        src={review.file.url}
                                        alt={review.file.name || 'Attached Image'}
                                        className="max-h-48 w-auto object-contain cursor-zoom-in group-hover:opacity-90 transition"
                                      />
                                    </a>
                                    <div className="p-2 bg-white border-t border-gray-100 flex items-center justify-between text-xs">
                                      <span className="text-gray-600 truncate max-w-[150px]" title={review.file.name}>{review.file.name || 'image.jpg'}</span>
                                      <a
                                        href={review.file.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-agrosaf-green hover:underline font-semibold ml-2"
                                      >
                                        View
                                      </a>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-3 p-3 bg-gray-50 border border-gray-200 rounded-lg shadow-sm hover:bg-gray-100 transition duration-150 max-w-sm">
                                    <div className="h-10 w-10 rounded bg-red-50 flex items-center justify-center flex-shrink-0 border border-red-100">
                                      <FileText className="h-5 w-5 text-red-500" />
                                    </div>
                                    <div className="overflow-hidden flex-1">
                                      <p className="text-xs font-bold text-gray-800 truncate" title={review.file.name}>{review.file.name || 'document.pdf'}</p>
                                      <p className="text-[10px] text-gray-500">
                                        {review.file.size ? `${(review.file.size / 1024 / 1024).toFixed(2)} MB` : 'PDF Document'}
                                      </p>
                                    </div>
                                    <a
                                      href={review.file.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs text-agrosaf-green font-semibold hover:underline ml-2"
                                    >
                                      Download
                                    </a>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {(() => {
                      const reviewsTotalItems = Number(customerReviewsPagination?.total || customerReviews.length || 0);
                      const reviewsTotalPages = Number(customerReviewsPagination?.totalPages || 1);
                      const effectiveReviewsLimit = customerReviewsPagination?.limit || reviewsLimit || 10;
                      const shouldShowReviewsPagination = !customerReviewsLoading && reviewsTotalPages > 1;

                      if (!shouldShowReviewsPagination) return null;

                      return (
                        <PaginationControls
                          currentPage={customerReviewsPagination?.page || reviewsPage || 1}
                          totalPages={reviewsTotalPages}
                          totalItems={reviewsTotalItems}
                          limit={effectiveReviewsLimit}
                          hasNextPage={(customerReviewsPagination?.page || reviewsPage || 1) < reviewsTotalPages}
                          hasPrevPage={(customerReviewsPagination?.page || reviewsPage || 1) > 1}
                          onPageChange={(page) => {
                            setReviewsPage(page);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                          onLimitChange={(limit) => {
                            setReviewsLimit(limit);
                            setReviewsPage(1);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        />
                      );
                    })()}
                  </>
                )}
              </div>
            </div>
          )}

          {activeTab === "assignment_history" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <h3 className="text-lg font-semibold text-gray-900">Customer Assignment History</h3>
              {(!rawCustomer.assignmentHistory || rawCustomer.assignmentHistory.length === 0) ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No assignment history found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned By</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...rawCustomer.assignmentHistory].reverse().map((history, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {rawCustomer.assignmentHistory.length - index}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                            <div className="font-semibold text-agrosaf-green">{getPerformerName(history.assignedTo)}</div>
                            <div className="text-[12px] text-gray-500 flex flex-col">
                              {history.assignedTo?.employeeId && <span>ID: {history.assignedTo.employeeId}</span>}
                              {history.assignedTo?.email && <span>{history.assignedTo.email}</span>}
                              {history.assignedTo?.role && <span className="capitalize text-gray-500">{history.assignedTo.role.replace(/_/g, ' ')}</span>}
                              {history.assignedTo?.department && <span className="text-gray-500">{history.assignedTo.department}</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="font-semibold">{getPerformerName(history.assignedBy)}</div>
                            <div className="text-[12px] text-gray-500 flex flex-col">
                              {history.assignedBy?.employeeId && <span>ID: {history.assignedBy.employeeId}</span>}
                              {history.assignedBy?.email && <span>{history.assignedBy.email}</span>}
                              {history.assignedBy?.role && <span className="capitalize text-gray-500">{history.assignedBy.role.replace(/_/g, ' ')}</span>}
                              {history.assignedBy?.department && <span>{history.assignedBy.department}</span>}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(history.assignedAt, { includeTime: true, includeSeconds: false })}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6 animate-in fade-in duration-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Customer Documents</h3>
                  <p className="text-sm text-gray-500 font-medium">View and manage uploaded certificates and documentation</p>
                </div>
              </div>

              {(!customer?.documents || customer.documents.length === 0) ? (
                <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center flex flex-col items-center">
                  <div className="h-20 w-20 bg-gray-50 rounded-full flex items-center justify-center mb-4">
                    <FileImage className="h-10 w-10 text-gray-300" />
                  </div>
                  <h4 className="text-lg font-bold text-gray-900 mb-2">No Documents Found</h4>
                  <p className="text-gray-500 max-w-sm mb-6 font-medium">There are no documents uploaded for this customer yet. You can add them from the edit profile page.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {customer.documents.map((doc, idx) => {
                    const isExpired = doc.expiry && new Date(doc.expiry) < new Date();
                    const isExpiringSoon = doc.expiry && !isExpired && (new Date(doc.expiry) - new Date()) < (30 * 24 * 60 * 60 * 1000);

                    return (
                      <div key={doc.id || idx} className="bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden group">
                        <div className="p-6">
                          <div className="flex items-start justify-between mb-4">
                            <div className="h-12 w-12 bg-green-50 rounded-2xl flex items-center justify-center text-agrosaf-green group-hover:scale-110 transition-transform duration-300">
                              <FileText className="h-6 w-6" />
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {doc.expiry ? (
                                <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider flex items-center gap-1.5 ${isExpired
                                  ? 'bg-red-50 text-red-600 border border-red-100'
                                  : isExpiringSoon
                                    ? 'bg-amber-50 text-amber-600 border border-amber-100'
                                    : 'bg-green-50 text-green-600 border border-green-100'
                                  }`}>
                                  <Clock className="h-3 w-3" />
                                  {isExpired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Valid'}
                                </div>
                              ) : (
                                <div className="px-3 py-1 bg-gray-50 text-gray-500 border border-gray-100 rounded-full text-[10px] font-bold uppercase tracking-wider">
                                  No Expiry
                                </div>
                              )}
                              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{doc.method === 'link' ? 'Linked Content' : 'Local Upload'}</span>
                            </div>
                          </div>

                          <h4 className="text-base font-bold text-gray-900 mb-1 truncate group-hover:text-agrosaf-green transition-colors">{doc.type}</h4>
                          <div className="flex items-center gap-2 mb-4">
                            <Hash className="h-3.5 w-3.5 text-gray-400" />
                            <p className="text-sm font-semibold text-gray-500">{doc.number}</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-50">
                            <div>
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Expiry Date</p>
                              <p className={`text-sm font-bold ${isExpired ? 'text-red-500' : 'text-gray-900'}`}>{doc.expiry ? new Date(doc.expiry).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Lifetime'}</p>
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              {doc.url && (
                                <>
                                  <a
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2.5 bg-gray-50 text-gray-600 hover:bg-agrosaf-green hover:text-white rounded-xl transition-all duration-300 flex items-center justify-center group/btn"
                                    title="View / Open File"
                                  >
                                    {doc.method === 'link' ? <ExternalLink className="h-4.5 w-4.5" /> : <Eye className="h-4.5 w-4.5" />}
                                  </a>
                                  {doc.method === 'upload' && (
                                    <a
                                      href={doc.url}
                                      download
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="p-2.5 bg-gray-50 text-gray-600 hover:bg-agrosaf-green hover:text-white rounded-xl transition-all duration-300 flex items-center justify-center"
                                      title="Download File"
                                    >
                                      <Download className="h-4.5 w-4.5" />
                                    </a>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === "logs" && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <HistoryIcon className="h-5 w-5 text-gray-400" />
                  Status History Logs
                </h3>
              </div>

              {!customer?.statusHistory || customer.statusHistory.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <HistoryIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No status changes logged yet.</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200"></div>

                  <div className="space-y-8">
                    {[...customer.statusHistory].reverse().map((log, index) => (
                      <div key={index} className="relative pl-14">
                        {/* Timeline dot */}
                        <div className={`absolute left-2 top-0 w-8 h-8 rounded-full flex items-center justify-center border-4 border-white shadow-sm z-10 ${log.status === 'blocked' ? 'bg-red-500' : 'bg-green-500'
                          }`}>
                          {log.status === 'blocked' ? <LockIcon className="h-3 w-3 text-white" /> : <UnlockIcon className="h-3 w-3 text-white" />}
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
                            <div>
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${log.status === 'blocked' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                }`}>
                                Customer {log.status}
                              </span>
                              <div className="flex items-center text-xs text-gray-500 gap-2 mt-2">
                                <Clock className="h-3 w-3" />
                                {formatDate(log.changedAt, { includeTime: true })}
                              </div>
                            </div>

                            {log.changedBy && (
                              <div className="flex flex-col items-end text-right">
                                <div className="flex items-center gap-1.5 text-xs font-medium text-gray-900">
                                  <User className="h-3 w-3 text-gray-400" />
                                  <span>
                                    {typeof log.changedBy === 'object'
                                      ? `${log.changedBy.firstName || ''} ${log.changedBy.lastName || ''}`.trim() || log.changedBy.email
                                      : 'Admin'}
                                  </span>
                                  {log.changedBy.department && (
                                    <span className="bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider">
                                      {log.changedBy.department}
                                    </span>
                                  )}
                                </div>
                                {log.changedBy.email && typeof log.changedBy === 'object' && (
                                  <div className="flex items-center gap-1.5 text-[11px] text-gray-500 mt-0.5">
                                    <Mail className="h-3 w-3 text-gray-400" />
                                    <span>{log.changedBy.email}</span>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {log.reason || 'No reason provided'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab — lazy-loaded, only mounts (and fetches) when active */}
          {activeTab === "analytics" && (
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-24">
                  <div className="flex flex-col items-center gap-3 text-gray-400">
                    <div className="h-8 w-8 border-2 border-agrosaf-green border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm">Loading analytics…</p>
                  </div>
                </div>
              }
            >
              <CustomerAnalytics customerId={id} />
            </Suspense>
          )}

          {/* Documents Tab (removed as per requirements) */}
        </div>
      </div>

      {/* **NEW: Message Modal** */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                New Message
              </h3>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message Type
                </label>
                <div className="flex space-x-2">
                  {messageTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.value}
                        onClick={() => setMessageType(type.value)}
                        className={`flex items-center px-3 py-2 rounded-lg border ${messageType === type.value
                          ? "bg-blue-50 border-blue-300 text-blue-700"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        <Icon className="h-4 w-4 mr-1" />
                        {type.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Recipient
                </label>
                <input
                  type="text"
                  value={messageRecipient}
                  onChange={(e) => {
                    const sanitized = handleTextInputChange(e, null, 100);
                    setMessageRecipient(sanitized);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    messageType === "email" ? "Email address" : "Phone number"
                  }
                  maxLength={100}
                />
              </div>

              {messageType === "email" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Subject *
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={messageSubject}
                      onChange={(e) => {
                        const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.title);
                        setMessageSubject(sanitized);
                        if (errors.messageSubject) {
                          setErrors(prev => ({ ...prev, messageSubject: '' }));
                        }
                      }}
                      className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.messageSubject ? 'border-red-300' : 'border-gray-300'
                        }`}
                      placeholder="Email subject"
                      maxLength={FIELD_LIMITS.title}
                      required
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs text-gray-500">
                      {getCharacterCount(messageSubject, FIELD_LIMITS.title)}
                    </div>
                  </div>
                  {errors.messageSubject && (
                    <p className="text-xs text-red-600 mt-1">{errors.messageSubject}</p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message *
                </label>
                <div className="relative">
                  <textarea
                    value={messageContent}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.message);
                      setMessageContent(sanitized);
                      if (errors.messageContent) {
                        setErrors(prev => ({ ...prev, messageContent: '' }));
                      }
                    }}
                    rows={4}
                    className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.messageContent ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter your message..."
                    maxLength={FIELD_LIMITS.message}
                    required
                  />
                  <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                    {getCharacterCount(messageContent, FIELD_LIMITS.message)}
                  </div>
                </div>
                {errors.messageContent && (
                  <p className="text-xs text-red-600 mt-1">{errors.messageContent}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  setMessageContent("");
                  setMessageSubject("");
                  setMessageType("whatsapp");
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={isSavingActivity}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4 mr-2" />
                {isSavingActivity ? "Saving..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interaction Detail Modal */}
      {showInteractionModal && selectedInteraction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Interaction Details
              </h3>
              <button
                onClick={() => {
                  setShowInteractionModal(false);
                  setSelectedInteraction(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  {(() => {
                    const Icon = getActivityIcon(selectedInteraction.type);
                    const color = getActivityColor(selectedInteraction.type);
                    return <Icon className={`h-5 w-5 ${color}`} />;
                  })()}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 capitalize">
                    {capitalizeText(selectedInteraction.title || selectedInteraction.type || 'Interaction')}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(getActivityTime(selectedInteraction), { includeTime: true })}
                  </p>
                </div>
              </div>

              {selectedInteraction.description && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">Description</h4>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {selectedInteraction.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Type</h4>
                  <p className="text-sm text-gray-900 capitalize">
                    {capitalizeText(selectedInteraction.type || 'N/A')}
                  </p>
                </div>
                {selectedInteraction.outcome && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Outcome</h4>
                    <p className="text-sm text-gray-900 capitalize">
                      {capitalizeText(selectedInteraction.outcome)}
                    </p>
                  </div>
                )}
                {selectedInteraction.performedBy && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Performed By</h4>
                    <p className="text-sm text-gray-900">
                      {getPerformerName(selectedInteraction.performedBy)}
                    </p>
                  </div>
                )}
                {selectedInteraction.channel && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Channel</h4>
                    <p className="text-sm text-gray-900 capitalize">
                      {capitalizeText(selectedInteraction.channel)}
                    </p>
                  </div>
                )}
              </div>

              {/* Call-specific metadata */}
              {selectedInteraction.type === 'call' && selectedInteraction.callMetadata && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold text-gray-900">Call Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
                    {selectedInteraction.callMetadata.status && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                          Status
                        </span>
                        <span className="capitalize">
                          {selectedInteraction.callMetadata.status}
                        </span>
                      </div>
                    )}
                    {selectedInteraction.callMetadata.talkDuration &&
                      selectedInteraction.callMetadata.talkDuration > 0 && (
                        <div>
                          <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                            Talk Time
                          </span>
                          <span>
                            {formatDuration(selectedInteraction.callMetadata.talkDuration)}
                          </span>
                        </div>
                      )}
                    {selectedInteraction.callMetadata.startTime && (
                      <div>
                        <span className="text-xs font-semibold text-gray-500 uppercase block mb-0.5">
                          Started At
                        </span>
                        <span>
                          {formatDate(selectedInteraction.callMetadata.startTime, { includeTime: true })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* **NEW: Call Log Modal** */}
      {showCallLogModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Log Call</h3>
              <button
                onClick={() => {
                  setShowCallLogModal(false);
                  setErrors({});
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Direction
                </label>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCallDirection("outbound")}
                    className={`flex items-center px-3 py-2 rounded-lg border ${callDirection === "outbound"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Outbound Call
                  </button>
                  <button
                    onClick={() => setCallDirection("inbound")}
                    className={`flex items-center px-3 py-2 rounded-lg border ${callDirection === "inbound"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                  >
                    Inbound Call
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Duration (HH:MM:SS)
                  </label>
                  <input
                    type="text"
                    value={callDuration}
                    onChange={(e) => setCallDuration(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="00:05:30"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Call Outcome
                  </label>
                  <select
                    value={callOutcome}
                    onChange={(e) => setCallOutcome(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select outcome</option>
                    {callOutcomes.map((outcome) => (
                      <option key={outcome.value} value={outcome.value}>
                        {outcome.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Call Notes *
                </label>
                <div className="relative">
                  <textarea
                    value={callNotes}
                    onChange={(e) => {
                      const sanitized = handleTextInputChange(e, null, FIELD_LIMITS.notes);
                      setCallNotes(sanitized);
                      if (errors.callNotes) {
                        setErrors(prev => ({ ...prev, callNotes: '' }));
                      }
                    }}
                    rows={4}
                    className={`w-full px-3 py-2 pr-16 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.callNotes ? 'border-red-300' : 'border-gray-300'
                      }`}
                    placeholder="Enter call notes and summary..."
                    maxLength={FIELD_LIMITS.notes}
                    required
                  />
                  <div className="absolute right-2 bottom-2 text-xs text-gray-500">
                    {getCharacterCount(callNotes, FIELD_LIMITS.notes)}
                  </div>
                </div>
                {errors.callNotes && (
                  <p className="text-xs text-red-600 mt-1">{errors.callNotes}</p>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="followUp"
                  checked={followUpRequired}
                  onChange={(e) => setFollowUpRequired(e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="followUp"
                  className="text-sm font-medium text-gray-700"
                >
                  Follow-up required
                </label>
              </div>

              {followUpRequired && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Follow-up Date
                  </label>
                  <input
                    type="datetime-local"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCallLogModal(false);
                  setCallDirection("outbound");
                  setCallDuration("");
                  setCallNotes("");
                  setCallOutcome("");
                  setFollowUpRequired(false);
                  setFollowUpDate("");
                  setErrors({});
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveCallLog}
                disabled={isSavingActivity}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <PhoneCall className="h-4 w-4 mr-2" />
                {isSavingActivity ? "Saving..." : "Save Call Log"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSendEmailModal && (
        <SendEmailModal
          isOpen={showSendEmailModal}
          onClose={() => setShowSendEmailModal(false)}
          customerEmail={customer?.email || ""}
          customerId={id}
          customerName={customer?.name || ""}
          customerProfileImage={customer?.profileImage}
          onSent={handleEmailSentActivity}
        />
      )}
      <WhatsAppModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        onSend={handleWhatsAppSend}
        isSending={isSendingWhatsApp}
        hideTypeSelection={true}
        quoteId={null}
      />
      <InvoiceDetailsModal
        isOpen={isInvoiceModalOpen}
        onClose={() => {
          setIsInvoiceModalOpen(false);
          setSelectedInvoice(null);
        }}
        invoice={selectedInvoice}
        customerId={id}
      />
      <PaymentDetailsModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
      <AddPaymentModal
        isOpen={showAddPaymentModal}
        customerId={id}
        initialInvoiceId={paymentInvoiceId}
        onClose={closeAddPaymentModal}
        onSuccess={handlePaymentSaved}
      />
    </div>
  );
};

/**
 * Modal to display detailed invoice information including line items and payments
 */
const InvoiceDetailsModal = ({ isOpen, onClose, invoice, customerId }) => {
  const [lineItemsOpen, setLineItemsOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  const {
    data: invoiceDetail,
    isLoading: detailLoading,
    isError: detailError,
    error: detailQueryError
  } = useQuery({
    queryKey: ['customerInvoiceDetail', customerId, invoice?._id],
    queryFn: async () => {
      const result = await customerService.getCustomerInvoiceById(customerId, invoice._id);
      if (!result.success) {
        throw new Error(result.error || 'Failed to load invoice details');
      }
      return result.invoice;
    },
    enabled: isOpen && !!customerId && !!invoice?._id
  });

  useEffect(() => {
    if (isOpen) {
      setLineItemsOpen(false);
      setPaymentsOpen(false);
    } else {
      setSelectedPayment(null);
      setIsPaymentModalOpen(false);
    }
  }, [isOpen, invoice?._id]);

  if (!isOpen || !invoice) return null;

  const detail = invoiceDetail || invoice;
  const lineItems = Array.isArray(invoiceDetail?.lineItems) ? invoiceDetail.lineItems : [];
  const payments = Array.isArray(invoiceDetail?.payments) ? invoiceDetail.payments : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-8 py-5 border-b border-gray-100 flex items-start justify-between bg-white">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <FileText className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">#{detail.invoiceNumber}</span>
                <span className="text-gray-300">•</span>
                <span className="text-xs font-medium text-gray-500">
                  {detail.invoiceDate ? formatDate(detail.invoiceDate, { includeTime: false }) : '--'}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-all text-gray-400 hover:text-gray-600 group"
          >
            <X className="h-6 w-6 group-hover:rotate-90 transition-transform duration-200" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto flex-1 custom-scrollbar space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 bg-gradient-to-br from-blue-50 to-white rounded-2xl border border-blue-100/50 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-110 transition-transform">
                <IndianRupee className="h-12 w-12 text-blue-600" />
              </div>
              <p className="text-[11px] text-blue-600 uppercase font-bold tracking-widest mb-2">Total Amount</p>
              <p className="text-2xl font-black text-blue-900">₹{formatIndianAmount(detail.totalAmount ?? 0)}</p>
            </div>
            <div className="p-5 bg-gradient-to-br from-teal-50 to-white rounded-2xl border border-teal-100/50 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-110 transition-transform">
                <CheckCircle className="h-12 w-12 text-teal-600" />
              </div>
              <p className="text-[11px] text-teal-600 uppercase font-bold tracking-widest mb-2">Paid Amount</p>
              <p className="text-2xl font-black text-teal-900">₹{formatIndianAmount(detail.paidAmount ?? 0)}</p>
            </div>
            <div className="p-5 bg-gradient-to-br from-red-50 to-white rounded-2xl border border-red-100/50 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:scale-110 transition-transform">
                <AlertTriangle className="h-12 w-12 text-red-600" />
              </div>
              <p className="text-[11px] text-red-600 uppercase font-bold tracking-widest mb-2">Outstanding Amount</p>
              <p className="text-2xl font-black text-red-900">₹{formatIndianAmount(detail.outstandingAmount ?? 0)}</p>
            </div>
          </div>

          {detailLoading && (
            <div className="flex items-center justify-center gap-2 py-6 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-agrosaf-green" />
              Loading invoice details...
            </div>
          )}

          {detailError && (
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              {detailQueryError?.message || 'Failed to load invoice details'}
            </div>
          )}

          {/* Products & Line Items (collapsible) */}
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <button
              type="button"
              onClick={() => setLineItemsOpen((open) => !open)}
              className="w-full px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-tight flex items-center gap-2">
                <ShoppingCart className="h-4 w-4 text-gray-500" />
                Products & Line Items
              </h4>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 bg-gray-200/50 text-gray-600 text-[10px] font-bold rounded-md uppercase">
                  {detailLoading ? '...' : `${lineItems.length} Items`}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${lineItemsOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {lineItemsOpen && (
              <div className="overflow-x-auto">
                {detailLoading ? (
                  <div className="py-10 text-center text-sm text-gray-500">Loading line items...</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/30">
                      <tr>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Product Info</th>
                        <th className="px-6 py-4 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Batch Info</th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">Quantity</th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">Unit Rate</th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">Discount</th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">Net Amount</th>
                        <th className="px-6 py-4 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">GST</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 bg-white">
                      {lineItems.length > 0 ? (
                        lineItems.map((item, idx) => (
                          <tr key={idx} className="hover:bg-blue-50/30 transition-colors group">
                            <td className="px-6 py-4 text-xs whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="text-gray-900 font-bold group-hover:text-blue-700 transition-colors">{item.pName || 'Unknown Product'}</span>
                                <span className="text-[10px] text-gray-400 mt-0.5">PID: #{item.pid}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-xs text-gray-500 uppercase whitespace-nowrap font-medium">
                              {item.batch || <span className="text-gray-300 italic">No Batch</span>}
                            </td>
                            <td className="px-6 py-4 text-xs text-gray-900 text-right whitespace-nowrap font-bold">
                              {item.qty}
                              {item.freeQty > 0 && (
                                <span className="block text-[10px] text-green-600 font-bold mt-0.5">+{item.freeQty} Free</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-xs text-gray-900 text-right whitespace-nowrap font-medium">₹{formatIndianNumber(item.rate)}</td>
                            <td className="px-6 py-4 text-xs text-right whitespace-nowrap">
                              {item.discount > 0 ? (
                                <span className="text-blue-600 font-bold bg-blue-50 px-2 py-1 rounded border border-blue-100">
                                  {item.discount}%
                                </span>
                              ) : (
                                <span className="text-gray-300">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-xs font-black text-gray-900 text-right whitespace-nowrap">₹{formatIndianAmount(item.amount)}</td>
                            <td className="px-6 py-4 text-xs text-purple-600 text-right whitespace-nowrap font-bold italic">{item.gstPct}%</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="px-6 py-16 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <Package className="h-10 w-10 text-gray-200" />
                              <p className="text-sm text-gray-400 font-medium">No detailed line items available for this invoice.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {/* Payments (collapsible) */}
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
            <button
              type="button"
              onClick={() => setPaymentsOpen((open) => !open)}
              className="w-full px-6 py-4 bg-gray-50/50 border-b border-gray-100 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <h4 className="text-sm font-bold text-gray-800 uppercase tracking-tight flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-gray-500" />
                Payments
              </h4>
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-1 bg-gray-200/50 text-gray-600 text-[10px] font-bold rounded-md uppercase">
                  {detailLoading ? '...' : `${payments.length} Payments`}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${paymentsOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>
            {paymentsOpen && (
              <div className="overflow-x-auto">
                {detailLoading ? (
                  <div className="py-10 text-center text-sm text-gray-500">Loading payments...</div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50/30">
                      <tr>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Payment ID</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Invoice</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Invoice Amount</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Received</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Outstanding</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Method</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-400 uppercase tracking-wider">Status</th>
                        <th className="px-4 py-3 text-right text-[11px] font-bold text-gray-400 uppercase tracking-wider">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50 bg-white">
                      {payments.length > 0 ? (
                        payments.map((payment) => (
                          <tr key={payment._id} className="hover:bg-green-50/30 transition-colors">
                            <td className="px-4 py-3 text-xs font-semibold text-gray-900 whitespace-nowrap">
                              {payment.paymentNumber || '--'}
                            </td>
                            <td className="px-4 py-3 text-xs font-medium text-gray-900 whitespace-nowrap">
                              {payment.invoiceNumber || detail.invoiceNumber || '--'}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-900 whitespace-nowrap">
                              ₹{formatIndianAmount(payment.invoiceAmount ?? detail.totalAmount ?? 0)}
                            </td>
                            <td className="px-4 py-3 text-xs font-semibold text-teal-700 whitespace-nowrap">
                              ₹{formatIndianAmount(payment.amountReceived || 0)}
                            </td>
                            <td className="px-4 py-3 text-xs font-semibold text-red-700 whitespace-nowrap">
                              ₹{formatIndianAmount(payment.outstandingAfterPayment || 0)}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                              {formatPaymentMethodLabel(payment.paymentMethod)}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                              {payment.paymentDate
                                ? formatDate(payment.paymentDate, { includeTime: true })
                                : '--'}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full ${getPaymentStatusClasses(payment.paymentStatus)}`}>
                                {payment.paymentStatus || '--'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedPayment(payment);
                                  setIsPaymentModalOpen(true);
                                }}
                                title="View Details"
                                aria-label="View payment details"
                                className="inline-flex items-center justify-center p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={9} className="px-6 py-16 text-center">
                            <div className="flex flex-col items-center gap-3">
                              <IndianRupee className="h-10 w-10 text-gray-200" />
                              <p className="text-sm text-gray-400 font-medium">No payments recorded for this invoice.</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
          <Button
            onClick={onClose}
            variant="secondary"
            className="px-8 font-bold text-xs uppercase tracking-widest h-11"
          >
            Close
          </Button>
        </div>
      </div>

      <PaymentDetailsModal
        isOpen={isPaymentModalOpen}
        onClose={() => {
          setIsPaymentModalOpen(false);
          setSelectedPayment(null);
        }}
        payment={selectedPayment}
      />
    </div>
  );
};

/**
 * Modal to display full payment details
 */
const PaymentDetailsModal = ({ isOpen, onClose, payment }) => {
  if (!isOpen || !payment) return null;

  const formatRoleLabel = (role) => {
    if (!role) return '--';
    return String(role)
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const recordedByName = payment.createdBy
    ? `${payment.createdBy.firstName || ''} ${payment.createdBy.lastName || ''}`.trim() || '--'
    : '--';

  const detailRows = [
    { label: 'Payment ID', value: payment.paymentNumber || '--' },
    { label: 'Invoice', value: payment.invoiceNumber || '--' },
    { label: 'Invoice Amount', value: `₹${formatIndianAmount(payment.invoiceAmount || 0)}` },
    { label: 'Received', value: `₹${formatIndianAmount(payment.amountReceived || 0)}` },
    { label: 'Outstanding Before', value: payment.previousOutstanding != null ? `₹${formatIndianAmount(payment.previousOutstanding)}` : '--' },
    { label: 'Outstanding After', value: `₹${formatIndianAmount(payment.outstandingAfterPayment || 0)}` },
    { label: 'Total Received After', value: payment.totalReceivedAfterPayment != null ? `₹${formatIndianAmount(payment.totalReceivedAfterPayment)}` : '--' },
    { label: 'Method', value: formatPaymentMethodLabel(payment.paymentMethod) },
    {
      label: 'Date',
      value: payment.paymentDate ? formatDate(payment.paymentDate, { includeTime: true }) : '--'
    },
    { label: 'Payment Reference', value: payment.paymentReference || '--' },
    { label: 'Source', value: payment.source || '--' },
    {
      label: 'Recorded At',
      value: payment.createdAt ? formatDate(payment.createdAt, { includeTime: true }) : '--'
    },
    { label: 'Remarks', value: payment.remarks || '--' }
  ];

  const recordedByRows = [
    { label: 'Name', value: recordedByName },
    { label: 'Email', value: payment.createdBy?.email || '--' },
    { label: 'Employee ID', value: payment.createdBy?.employeeId || '--' },
    { label: 'Role', value: formatRoleLabel(payment.createdBy?.role) },
    { label: 'Department', value: payment.createdBy?.department || '--' }
  ];

  const renderDetailCard = (row) => (
    <div key={row.label} className="rounded-xl border border-gray-100 bg-gray-50/60 px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">{row.label}</p>
      <p className="text-sm font-medium text-gray-900 break-words whitespace-pre-wrap">{row.value}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 flex items-start justify-between bg-white">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <IndianRupee className="h-6 w-6 text-agrosaf-green" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">Payment Details</h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-xs font-bold text-gray-400 uppercase tracking-wider">
                  {payment.paymentNumber || 'Payment'}
                </span>
                <span className="text-gray-300">•</span>
                <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getPaymentStatusClasses(payment.paymentStatus)}`}>
                  {payment.paymentStatus || '--'}
                </span>
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-all text-gray-400 hover:text-gray-600 group"
            aria-label="Close"
          >
            <X className="h-6 w-6 group-hover:rotate-90 transition-transform duration-200" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 custom-scrollbar space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 rounded-xl bg-blue-50 border border-blue-100">
              <p className="text-[11px] text-blue-600 uppercase font-bold tracking-wider mb-1">Invoice Amount</p>
              <p className="text-lg font-bold text-blue-900">₹{formatIndianAmount(payment.invoiceAmount || 0)}</p>
            </div>
            <div className="p-4 rounded-xl bg-teal-50 border border-teal-100">
              <p className="text-[11px] text-teal-600 uppercase font-bold tracking-wider mb-1">Received</p>
              <p className="text-lg font-bold text-teal-900">₹{formatIndianAmount(payment.amountReceived || 0)}</p>
            </div>
            <div className="p-4 rounded-xl bg-red-50 border border-red-100">
              <p className="text-[11px] text-red-600 uppercase font-bold tracking-wider mb-1">Outstanding After</p>
              <p className="text-lg font-bold text-red-900">₹{formatIndianAmount(payment.outstandingAfterPayment || 0)}</p>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-800 mb-3">Payment Information</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {detailRows.map(renderDetailCard)}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-800 mb-3">Recorded By</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recordedByRows.map(renderDetailCard)}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end">
          <Button
            onClick={onClose}
            variant="secondary"
            className="px-8 font-bold text-xs uppercase tracking-widest h-11"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Customer360;
